import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import axios from "axios";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import SoftSelect from "components/SoftSelect";
import SoftSnackbar from "components/SoftSnackbar";
import FlagLoader from "layouts/flagloader";
import { API_URL } from "config";
import { useSoftUIController } from "context";
import { useAuth } from "../../AuthContext";
import { formatCountryOptionLabel, makeCountryOption } from "utils/countryOptions";
import Swal from "sweetalert2";

import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DownloadIcon from "@mui/icons-material/Download";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import Card from "@mui/material/Card";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import Drawer from "@mui/material/Drawer";
import FormControlLabel from "@mui/material/FormControlLabel";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Tooltip from "@mui/material/Tooltip";

const emptyCompanyForm = {
  vendor_name: "",
  vendor_email: "",
  vendor_mobile: "",
  company_name: "",
  official_website: "",
  office_phone_number: "",
  billing_setting_id: "",
  invoicing_company_id: "",
  billing_currency_id: "",
  billing_cycle_days: "",
  country: "",
  se_zone: false,
  pan_number: "",
  city: "",
  state: "",
  postal_code: "",
  company_address: "",
  shipping_address: "",
  note: "",
  company_logo: null,
  existing_company_logo: "",
  complete_redirect_url: "",
  terminate_redirect_url: "",
  over_quota_redirect_url: "",
  quality_terminate_redirect_url: "",
  vendor_category_id: "",
  vendor_sub_category_id: "",
  status: "Active",
  vendor_type: "external",
};

// Vendor tax identity now comes from the global Company master.
// Keep the legacy tax editor code available for a future rollback, but do not render or submit it.
const PARTY_TAX_FIELDS_ENABLED = false;

const emptyBillingTax = {
  id: null,
  billing_tax_name_id: "",
  tax_name: "",
  tax_number: "",
};

const emptyContact = {
  id: null,
  name: "",
  email: "",
  login_email: "",
  password: "",
  profile_picture: null,
  existing_profile_picture: "",
  mobile: "",
  login_allowed: false,
  status: "Active",
  receive_email_notifications: false,
  access_role_ids: [],
};

const standardTaxNames = ["gst", "vat", "lut"];
const billingCurrencyOrder = ["USD", "INR", "GBP", "EURO", "AED", "SGD"];

const parseStoredPermissions = () => {
  try {
    const stored = localStorage.getItem("permissions");
    if (!stored) return [];
    const parsed = JSON.parse(stored);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const headerCellSx = {
  backgroundColor: "#f8f9fa",
  color: "#8392ab",
  fontSize: "12px",
  fontWeight: 400,
  letterSpacing: 0,
  textTransform: "capitalize",
  borderBottom: "1px solid #e2e8f0",
  py: 0.75,
  px: 0.6,
  whiteSpace: "nowrap",
  lineHeight: 1.15,
};

const bodyCellSx = {
  color: "#334155",
  fontSize: "10px",
  fontWeight: 400,
  borderBottom: "1px solid #edf2f7",
  py: 0.45,
  px: 0.6,
  verticalAlign: "middle",
  lineHeight: 1.15,
};

const sectionSx = {
  border: "1px solid #e6edf5",
  borderRadius: "8px",
  p: 0.75,
  pr: { xs: 0.75, md: 4 },
  mb: 0.5,
  backgroundColor: "#fff",
  "& .MuiInputBase-root": {
    minHeight: "32px !important",
    height: "32px",
    width: "100%",
    borderRadius: "6px",
    padding: "4px 10px !important",
    fontSize: "12px",
  },
  "& .MuiInputBase-input": {
    height: "22px",
    padding: "0 !important",
    fontSize: "12px !important",
    lineHeight: "22px",
  },
};

const formLabelSx = {
  fontSize: "11px",
  fontWeight: 700,
  color: "#1e293b",
  lineHeight: 1.2,
  mb: 0.25,
};

const formSelectSx = {
  "--soft-select-height": "32px",
  "--soft-select-value-height": "30px",
  "--soft-select-font-size": "12px",
  "--soft-select-value-px": "10px",
  "--soft-select-text-top": "50%",
  "--soft-select-indicator-padding": "6px",
  "--soft-select-indicator-size": "12px",
  "--soft-select-menu-padding": "4px",
  "--soft-select-option-padding": "5px 8px",
  "--soft-select-border-radius": "6px",
};

const drawerActionButtonSx = {
  minHeight: 32,
  height: 32,
  px: 2,
  py: "0 !important",
  fontSize: "12px !important",
  lineHeight: 1,
  textTransform: "none",
  borderRadius: "6px",
};

const fileButtonSx = {
  ...drawerActionButtonSx,
  minHeight: 28,
  height: 28,
  px: 1.5,
};

const listSearchInputSx = {
  minHeight: "24px !important",
  padding: "1px 8px !important",
  "& input": {
    height: "20px !important",
    fontSize: "10px !important",
  },
  "& input[type='date']": {
    lineHeight: "20px !important",
  },
  "& input[type='date']::-webkit-calendar-picker-indicator": {
    width: "12px",
    height: "12px",
    padding: 0,
    margin: 0,
  },
};

const listSearchControlSx = {
  width: { xs: "100%", sm: "9rem" },
  flex: { xs: "1 1 100%", sm: "0 0 9rem" },
};

const listSearchSelectSx = {
  "--soft-select-height": "24px",
  "--soft-select-value-height": "22px",
  "--soft-select-font-size": "10px",
  "--soft-select-value-px": "8px",
  "--soft-select-text-top": "50%",
  "--soft-select-indicator-padding": "3px",
  "--soft-select-indicator-size": "12px",
  "--soft-select-menu-padding": "4px",
  "--soft-select-option-padding": "4px 8px",
  "--soft-select-border-radius": "6px",
};

const bulkControlSx = {
  ...listSearchControlSx,
};

const bulkButtonSx = {
  minHeight: 24,
  height: 24,
  px: 1.25,
  py: "0 !important",
  fontSize: "10px !important",
  lineHeight: 1,
  textTransform: "none",
  borderRadius: "6px",
};

const toolbarButtonSx = {
  minHeight: 24,
  height: 24,
  px: 1.25,
  py: "0 !important",
  fontSize: "10px !important",
  lineHeight: 1,
  textTransform: "none",
  borderRadius: "6px",
  "& .MuiButton-startIcon": {
    mr: 0.5,
  },
  "& .MuiSvgIcon-root": {
    fontSize: "14px !important",
  },
};

const listExportButtonSx = {
  ...toolbarButtonSx,
};

const fieldWithActionSx = {
  position: "relative",
  width: "100%",
};

const fieldActionButtonSx = {
  position: "absolute",
  top: "50%",
  right: { xs: 6, md: -42 },
  transform: "translateY(-50%)",
  width: 30,
  height: 30,
  p: 0,
  zIndex: 1,
};

const fieldActionsSx = {
  position: "absolute",
  top: "50%",
  right: { xs: 6, md: -78 },
  transform: "translateY(-50%)",
  display: "flex",
  gap: 0.5,
  zIndex: 1,
};

const fieldActionIconSx = {
  width: 30,
  height: 30,
  p: 0,
};

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

const toSelectValue = (value) => (value === null || value === undefined ? "" : String(value));

const customSwalStyle = `
  .custom-swal-popup {
    border-radius: 12px !important;
    padding: 24px !important;
  }
  .custom-swal-title {
    font-size: 20px !important;
    font-weight: 600 !important;
    color: #344767 !important;
  }
  .custom-swal-input {
    border-radius: 8px !important;
    border: 1px solid #d2d6da !important;
    padding: 10px 15px !important;
    font-size: 14px !important;
    color: #495057 !important;
  }
  .custom-swal-input:focus {
    border-color: #35a4d4 !important;
    outline: 0;
    box-shadow: 0 0 0 2px rgba(23, 201, 100, 0.2) !important;
  }
  .custom-swal-actions {
    margin-top: 24px !important;
    display: flex !important;
    justify-content: center !important;
    gap: 16px !important;
    width: 100% !important;
  }
  .custom-swal-confirm-btn, .custom-swal-cancel-btn {
    margin: 0 !important;
    min-width: 120px !important;
    padding: 10px 24px !important;
    font-size: 14px !important;
    font-weight: 600 !important;
    border-radius: 8px !important;
    cursor: pointer !important;
    transition: all 0.15s ease-in-out !important;
    text-transform: uppercase !important;
    letter-spacing: 0.5px !important;
  }
  .custom-swal-confirm-btn {
    background-image: linear-gradient(310deg, #2152ff 0%, #02c6f3 100%) !important;
    color: #fff !important;
    border: none !important;
    box-shadow: 0 4px 7px -1px rgba(0, 0, 0, 0.11), 0 2px 4px -1px rgba(0, 0, 0, 0.07) !important;
  }
  .custom-swal-confirm-btn:hover {
    transform: translateY(-1px) !important;
    box-shadow: 0 7px 14px rgba(50, 50, 93, 0.1), 0 3px 6px rgba(0, 0, 0, 0.08) !important;
  }
  .custom-swal-cancel-btn {
    background-color: transparent !important;
    color: #344767 !important;
    border: 1px solid #344767 !important;
  }
  .custom-swal-cancel-btn:hover {
    background-color: rgba(52, 71, 103, 0.05) !important;
  }
`;

function Vendors() {
  const token = localStorage.getItem("authToken");
  const navigate = useNavigate();
  const { isAuthReady, permissions = [], userPermission = [] } = useAuth();
  const [controller] = useSoftUIController();
  const { miniSidenav } = controller;
  const drawerDesktopOffset = miniSidenav ? 120 : 274;

  const [vendors, setVendors] = useState([]);
  const [vendorOptions, setVendorOptions] = useState([]);
  const [clientOptions, setClientOptions] = useState([]);
  const [clientOptionsLoading, setClientOptionsLoading] = useState(false);
  const [clientCpiRules, setClientCpiRules] = useState([]);
  const [clientRulesTouched, setClientRulesTouched] = useState(false);
  const [categories, setCategories] = useState([]);
  const [billingSettings, setBillingSettings] = useState([]);
  const [invoicingCompanies, setInvoicingCompanies] = useState([]);
  const [invoicingCompaniesLoading, setInvoicingCompaniesLoading] = useState(false);
  const [billingCurrencies, setBillingCurrencies] = useState([]);
  const [billingCycles, setBillingCycles] = useState([]);
  const [billingTaxNames, setBillingTaxNames] = useState([]);
  const [vendorCountries, setVendorCountries] = useState([]);
  const [vendorStates, setVendorStates] = useState([]);
  const [vendorCities, setVendorCities] = useState([]);
  const [vendorAccessRoles, setVendorAccessRoles] = useState([]);
  const [pagination, setPagination] = useState({ currentPage: 1, lastPage: 1, from: 0, to: 0, total: 0 });
  const [filters, setFilters] = useState({
    start_date: "",
    end_date: "",
    vendor_id: [],
    category_id: [],
    vendor_type: [],
    search: "",
  });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState("add");
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [selectedVendors, setSelectedVendors] = useState([]);
  const [bulkAction, setBulkAction] = useState("");
  const [bulkStatus, setBulkStatus] = useState("Active");
  const [isExporting, setIsExporting] = useState(false);
  const [companyForm, setCompanyForm] = useState(emptyCompanyForm);
  const [billingTaxes, setBillingTaxes] = useState([{ ...emptyBillingTax }]);
  const [contacts, setContacts] = useState([{ ...emptyContact }]);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuVendor, setMenuVendor] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", color: "success" });

  const grantedPermissions = useMemo(
    () => Array.from(new Set([...permissions, ...userPermission, ...parseStoredPermissions()])),
    [permissions, userPermission]
  );

  const hasVendorPermission = useCallback((...requiredPermissions) => {
    if (grantedPermissions.includes("access.system.full_access")) return true;
    const required = requiredPermissions.flat().filter(Boolean);
    return required.some((permission) => grantedPermissions.includes(permission));
  }, [grantedPermissions]);

  const hasAccessPermission = useCallback(
    (permission) =>
      grantedPermissions.includes("access.system.full_access") || grantedPermissions.includes(permission),
    [grantedPermissions]
  );

  const canAccessVendors = hasVendorPermission("vendors.list.view");
  const canCreateVendor = hasVendorPermission("vendors.actions.create");
  const canUpdateVendor = hasVendorPermission("vendors.actions.update");
  const canDeleteVendor = hasVendorPermission("vendors.actions.delete");
  const canExportVendors = hasVendorPermission("vendors.actions.export");
  const canBulkDeleteVendors = hasVendorPermission("vendors.actions.bulk_delete");
  const canBulkStatusVendors = hasVendorPermission("vendors.actions.bulk_status");
  const canViewVendorPage = hasVendorPermission("vendors.view_page.view");
  const canOpenVendorPage = canViewVendorPage;
  const canUseBulkActions = canBulkDeleteVendors || canBulkStatusVendors;
  const canCreateCategory = hasVendorPermission("vendors.actions.create_category");
  const canCreateBillingTaxName = hasVendorPermission("vendors.actions.create_billing_tax_name");
  const canCreateBillingCurrency = hasVendorPermission("vendors.actions.create_billing_currency");
  const canCreateBillingCycle = hasVendorPermission("vendors.actions.create_billing_cycle");
  const canCreateState = hasVendorPermission("vendors.actions.create_state");
  const canCreateCity = hasVendorPermission("vendors.actions.create_city");
  const canAddContact = hasVendorPermission("vendors.actions.add_contact");
  const canRemoveContact = hasVendorPermission("vendors.actions.remove_contact");
  const canAddTaxRow = hasVendorPermission("vendors.actions.add_tax_row");
  const canRemoveTaxRow = hasVendorPermission("vendors.actions.remove_tax_row");
  const canViewVendorField = useCallback(
    (field) => hasVendorPermission(`vendors.fields.${field}.view`),
    [hasVendorPermission]
  );
  const canViewContactField = useCallback(
    (field) => hasVendorPermission(`vendors.contacts.fields.${field}.view`),
    [hasVendorPermission]
  );
  const canViewContactAccessRoles =
    canViewContactField("access_roles") && hasAccessPermission("access.roles.view");
  const canAssignVendorRoles =
    canViewContactAccessRoles && hasAccessPermission("access.roles.assign_members");

  const showNameColumn =
    canViewVendorField("name") || canViewVendorField("company_name") || canViewVendorField("company_logo");
  const showEmailColumn = canViewVendorField("email");
  const showMobileColumn = canViewVendorField("mobile");
  const showCategoryColumn = canViewVendorField("vendor_category");
  const showVendorTypeColumn = canViewVendorField("vendor_type");
  const showStatusColumn = canViewVendorField("status");
  const showCreatedColumn = canViewVendorField("created_at");
  const canUseRowActions =
    canViewVendorField("actions") && (canOpenVendorPage || canUpdateVendor || canDeleteVendor);
  const canViewAnyContactField = [
    "name",
    "email",
    "mobile",
    "profile_picture",
    "status",
    "receive_email_notifications",
    "login_email",
    "password",
    "login_allowed",
    "access_roles",
  ].some((field) => canViewContactField(field));
  const canViewAnyRedirectField = [
    "complete_redirect_url",
    "terminate_redirect_url",
    "over_quota_redirect_url",
    "quality_terminate_redirect_url",
  ].some((field) => canViewVendorField(field));
  const visibleTableColumnCount = [
    canUseBulkActions,
    showNameColumn,
    showEmailColumn,
    showMobileColumn,
    showCategoryColumn,
    showVendorTypeColumn,
    showStatusColumn,
    showCreatedColumn,
    canUseRowActions,
  ].filter(Boolean).length || 1;

  const requestConfig = useMemo(
    () => ({
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }),
    [token]
  );

  const rootCategories = useMemo(
    () => categories.filter((category) => !category.parent_id),
    [categories]
  );

  const vendorFilterOptions = useMemo(
    () =>
      vendorOptions.map((vendor) => ({
        value: String(vendor.id),
        label: vendor.name || vendor.company_name || vendor.email || `Vendor ${vendor.id}`,
      })),
    [vendorOptions]
  );
  const categoryFilterOptions = useMemo(
    () => rootCategories.map((category) => ({ value: String(category.id), label: category.name })),
    [rootCategories]
  );
  const selectedFilterOptions = (options, value) => {
    const selectedValues = new Set((Array.isArray(value) ? value : value ? [value] : []).map(String));
    return options.filter((option) => selectedValues.has(String(option.value)));
  };
  const selectedVendorFilter = selectedFilterOptions(vendorFilterOptions, filters.vendor_id);
  const selectedCategoryFilter = selectedFilterOptions(categoryFilterOptions, filters.category_id);
  const bulkActionOptions = useMemo(
    () => [
      ...(canBulkStatusVendors ? [{ value: "change_status", label: "Change Status" }] : []),
      ...(canBulkDeleteVendors ? [{ value: "delete", label: "Delete" }] : []),
    ],
    [canBulkDeleteVendors, canBulkStatusVendors]
  );
  const bulkStatusOptions = useMemo(
    () => [
      { value: "Active", label: "Active" },
      { value: "Inactive", label: "Inactive" },
    ],
    []
  );
  const vendorTypeOptions = useMemo(
    () => [
      { value: "external", label: "External" },
      { value: "internal", label: "Internal" },
    ],
    []
  );
  const vendorAccessRoleOptions = useMemo(
    () =>
      vendorAccessRoles.map((role) => ({
        value: String(role.id),
        label: role.name || `Role ${role.id}`,
      })),
    [vendorAccessRoles]
  );
  const selectedBulkAction = bulkActionOptions.find((option) => option.value === bulkAction) || null;
  const selectedBulkStatus = bulkStatusOptions.find((option) => option.value === bulkStatus) || bulkStatusOptions[0];
  const selectedVendorTypeFilter = selectedFilterOptions(vendorTypeOptions, filters.vendor_type);
  const formatVendorType = (value) =>
    String(value || "external").toLowerCase() === "internal" ? "Internal" : "External";

  const normalizeCountry = (value) => String(value || "").trim().toLowerCase();
  const selectedVendorCountry = vendorCountries.find(
    (country) => normalizeCountry(country.name) === normalizeCountry(companyForm.country)
  );
  const selectedVendorState = vendorStates.find(
    (state) => String(state.name || "").trim().toLowerCase() === String(companyForm.state || "").trim().toLowerCase()
  );
  const selectedBillingSetting = billingSettings.find(
    (setting) => toSelectValue(setting.id) === toSelectValue(companyForm.billing_setting_id)
  );
  const isIndiaVendorCountry = ["india", "in"].includes(normalizeCountry(companyForm.country));
  const isIndiaInvoicingCountry = ["india", "in"].includes(normalizeCountry(selectedBillingSetting?.name));
  const invoicingCountryCode = isIndiaInvoicingCountry
    ? "IN"
    : normalizeCountry(selectedBillingSetting?.name) === "uae"
      ? "UAE"
      : "";
  const showVendorSeZone = isIndiaInvoicingCountry && isIndiaVendorCountry && canViewVendorField("se_zone");
  const showVendorInvoicingCompany = Boolean(invoicingCountryCode) && canViewVendorField("invoicing_company");
  const sortedBillingCurrencies = useMemo(
    () =>
      [...billingCurrencies].sort((a, b) => {
        const aOrder = billingCurrencyOrder.indexOf(String(a.code || "").toUpperCase());
        const bOrder = billingCurrencyOrder.indexOf(String(b.code || "").toUpperCase());
        const normalizedAOrder = aOrder === -1 ? billingCurrencyOrder.length : aOrder;
        const normalizedBOrder = bOrder === -1 ? billingCurrencyOrder.length : bOrder;

        if (normalizedAOrder !== normalizedBOrder) return normalizedAOrder - normalizedBOrder;
        return String(a.code || "").localeCompare(String(b.code || ""));
      }),
    [billingCurrencies]
  );
  const filteredTaxNames = useMemo(
    () =>
      billingTaxNames.filter((taxName) => {
        const normalizedName = String(taxName.name || "").trim().toLowerCase();
        const isStandard = standardTaxNames.includes(normalizedName);

        if (!isStandard) return true;
        return isIndiaVendorCountry ? ["gst", "vat"].includes(normalizedName) : normalizedName === "lut";
      }),
    [billingTaxNames, isIndiaVendorCountry]
  );
  const defaultTaxName =
    billingTaxNames.find(
      (taxName) =>
        String(taxName.name || "").trim().toLowerCase() === (isIndiaVendorCountry ? "gst" : "lut")
    ) ||
    filteredTaxNames[0] ||
    null;
  const taxNumberLabel = "Tax Number";

  const readOnly = drawerMode === "view";
  const drawerTitle = drawerMode === "add" ? "Add Vendor" : drawerMode === "edit" ? "Edit Vendor" : "View Vendor";

  const showMessage = (message, color = "success") => {
    setSnackbar({ open: true, message, color });
  };

  useEffect(() => {
    if (!drawerOpen || readOnly || companyForm.vendor_type !== "external") return undefined;
    const abortController = new AbortController();
    setClientOptionsLoading(true);
    axios.get(`${API_URL}/vendors/client-options`, {
      headers: { Authorization: `Bearer ${token}` },
      signal: abortController.signal,
    }).then(({ data }) => {
      setClientOptions(Array.isArray(data.clients) ? data.clients : []);
    }).catch((error) => {
      if (!abortController.signal.aborted) {
        setSnackbar({ open: true, color: "error", message: error.response?.data?.message || "Failed to load clients" });
      }
    }).finally(() => {
      if (!abortController.signal.aborted) setClientOptionsLoading(false);
    });
    return () => abortController.abort();
  }, [drawerOpen, readOnly, companyForm.vendor_type, token]);

  const handleSelectAll = (event) => {
    if (!canUseBulkActions) return;

    if (event.target.checked) {
      setSelectedVendors(vendors.map((c) => c.id));
    } else {
      setSelectedVendors([]);
    }
  };

  const handleSelectRow = (id) => {
    if (!canUseBulkActions) return;

    setSelectedVendors((prev) =>
      prev.includes(id) ? prev.filter((vendorId) => vendorId !== id) : [...prev, id]
    );
  };

  const handleExport = async () => {
    if (!canExportVendors) {
      showMessage("You do not have permission to export vendors", "error");
      return;
    }

    try {
      setIsExporting(true);
      const params = new URLSearchParams();
      params.set("order_by", "id");
      params.set("order", "DESC");

      Object.entries(filters).forEach(([key, value]) => {
        const filterValue = Array.isArray(value) ? value.filter(Boolean).join(",") : value;
        if (filterValue) {
          const apiKey = key === "search" ? "q" : key;
          params.set(apiKey, filterValue);
        }
      });

      const response = await axios.get(`${API_URL}/vendors/export?${params.toString()}`, {
        ...requestConfig,
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'vendors.csv');
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);

      showMessage("Export successful");
    } catch (error) {
      showMessage("Export failed", "error");
    } finally {
      setIsExporting(false);
    }
  };

  const handleApplyBulkAction = async () => {
    if (bulkAction === "delete" && !canBulkDeleteVendors) {
      showMessage("You do not have permission to bulk delete vendors", "error");
      return;
    }
    if (bulkAction === "change_status" && !canBulkStatusVendors) {
      showMessage("You do not have permission to bulk update vendors", "error");
      return;
    }

    if (selectedVendors.length === 0) {
      showMessage("Please select at least one vendor", "error");
      return;
    }
    if (!bulkAction) {
      showMessage("Please select a bulk action", "error");
      return;
    }

    try {
      setLoading(true);
      if (bulkAction === "delete") {
        await axios.post(`${API_URL}/vendors/bulk-delete`, { vendor_ids: selectedVendors }, requestConfig);
        showMessage("Selected vendors deleted successfully");
      } else if (bulkAction === "change_status") {
        await axios.post(`${API_URL}/vendors/bulk-status`, { vendor_ids: selectedVendors, status: bulkStatus }, requestConfig);
        showMessage(`Selected vendors marked as ${bulkStatus}`);
      }
      setSelectedVendors([]);
      setBulkAction("");
      fetchVendors();
    } catch (error) {
      showMessage(error.response?.data?.message || "Bulk action failed", "error");
    } finally {
      setLoading(false);
    }
  };

  const fetchVendors = async (page = pagination.currentPage) => {
    if (!canAccessVendors) return;

    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.set("per_page", "20");
      params.set("page", page);
      params.set("order_by", "id");
      params.set("order", "DESC");

      Object.entries(filters).forEach(([key, value]) => {
        const filterValue = Array.isArray(value) ? value.filter(Boolean).join(",") : value;
        if (filterValue) {
          const apiKey = key === "search" ? "q" : key;
          params.set(apiKey, filterValue);
        }
      });

      const response = await axios.get(`${API_URL}/vendors?${params.toString()}`, requestConfig);
      const pageData = response.data?.vendors || {};
      setVendors(pageData.data || []);
      setPagination({
        currentPage: pageData.current_page || 1,
        lastPage: pageData.last_page || 1,
        from: pageData.from || 0,
        to: pageData.to || 0,
        total: pageData.total || 0,
      });
    } catch (error) {
      showMessage(error.response?.data?.message || error.message || "Failed to load vendors", "error");
    } finally {
      setLoading(false);
    }
  };

  const fetchVendorOptions = async () => {
    if (!canAccessVendors) return;

    try {
      const response = await axios.get(`${API_URL}/vendors?per_page=500&order_by=name&order=ASC`, requestConfig);
      setVendorOptions(response.data?.vendors?.data || []);
    } catch (error) {
      showMessage(error.response?.data?.message || "Failed to load vendor filter", "error");
    }
  };

  const fetchVendorAccessRoles = async () => {
    if (!canViewContactAccessRoles) return;

    try {
      const response = await axios.get(`${API_URL}/access-roles?per_page=500&actor_type=vendor`, requestConfig);
      const rows = response.data?.roles?.data || response.data?.roles || response.data?.data || [];
      setVendorAccessRoles(Array.isArray(rows) ? rows.filter((role) => role.actor_type === "vendor") : []);
    } catch (error) {
      showMessage(error.response?.data?.message || "Failed to load vendor access roles", "error");
    }
  };

  const fetchVendorDetails = async (vendorId) => {
    const response = await axios.get(`${API_URL}/vendors/${vendorId}`, requestConfig);
    return response.data?.vendor || null;
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API_URL}/vendors-categories`, requestConfig);
      setCategories(response.data?.data || []);
    } catch (error) {
      showMessage(error.response?.data?.message || "Failed to load vendor categories", "error");
    }
  };

  const fetchBillingOptions = async () => {
    const requests = [
      axios.get(`${API_URL}/billing-settings`, requestConfig),
      axios.get(`${API_URL}/billing-currencies`, requestConfig),
      axios.get(`${API_URL}/billing-cycles`, requestConfig),
    ];
    if (PARTY_TAX_FIELDS_ENABLED) {
      requests.push(axios.get(`${API_URL}/billing-tax-names`, requestConfig));
    }
    const [settingsResponse, currenciesResponse, cyclesResponse, taxNamesResponse] =
      await Promise.allSettled(requests);

    if (settingsResponse.status === "fulfilled") {
      setBillingSettings(settingsResponse.value.data?.data || []);
    }

    if (currenciesResponse.status === "fulfilled") {
      setBillingCurrencies(currenciesResponse.value.data?.data || []);
    }

    if (cyclesResponse.status === "fulfilled") {
      setBillingCycles(cyclesResponse.value.data?.data || []);
    }

    if (taxNamesResponse?.status === "fulfilled") {
      setBillingTaxNames(taxNamesResponse.value.data?.data || []);
    }

    if ([settingsResponse, currenciesResponse, cyclesResponse, taxNamesResponse]
      .filter(Boolean)
      .some((result) => result.status === "rejected")) {
      showMessage("Some billing options failed to load", "error");
    }
  };

  const fetchVendorCountries = async () => {
    try {
      const response = await axios.get(`${API_URL}/vendor-countries`, requestConfig);
      setVendorCountries(response.data?.data || []);
    } catch (error) {
      showMessage(error.response?.data?.message || "Failed to load vendor countries", "error");
    }
  };

  const fetchVendorStates = async (countryName = companyForm.country) => {
    try {
      const params = new URLSearchParams();
      if (countryName) params.set("country_name", countryName);

      const response = await axios.get(`${API_URL}/vendor-states?${params.toString()}`, requestConfig);
      setVendorStates(response.data?.data || []);
    } catch (error) {
      showMessage(error.response?.data?.message || "Failed to load vendor states", "error");
    }
  };

  const fetchVendorCities = async (countryName = companyForm.country, stateName = companyForm.state) => {
    try {
      const params = new URLSearchParams();
      if (countryName) params.set("country_name", countryName);
      if (stateName) params.set("state_name", stateName);

      const response = await axios.get(`${API_URL}/vendor-cities?${params.toString()}`, requestConfig);
      setVendorCities(response.data?.data || []);
    } catch (error) {
      showMessage(error.response?.data?.message || "Failed to load vendor cities", "error");
    }
  };

  const fetchInvoicingCompanies = async (countryCode) => {
    if (!countryCode || !canViewVendorField("invoicing_company")) {
      setInvoicingCompanies([]);
      return;
    }

    try {
      setInvoicingCompaniesLoading(true);
      const response = await axios.get(
        `${API_URL}/invoicing-companies?country=${encodeURIComponent(countryCode)}`,
        requestConfig
      );
      setInvoicingCompanies(response.data?.data || []);
    } catch (error) {
      setInvoicingCompanies([]);
      showMessage(error.response?.data?.message || "Failed to load invoicing companies", "error");
    } finally {
      setInvoicingCompaniesLoading(false);
    }
  };

  useEffect(() => {
    if (!isAuthReady) return;
    if (!canAccessVendors) {
      setLoading(false);
      return;
    }

    fetchCategories();
    fetchBillingOptions();
    fetchVendorCountries();
    fetchVendorOptions();
    fetchVendorAccessRoles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthReady, canAccessVendors, canViewContactAccessRoles]);

  useEffect(() => {
    if (!isAuthReady || !canAccessVendors) return;
    if (!companyForm.country) {
      setVendorStates([]);
      setVendorCities([]);
      return;
    }

    fetchVendorStates(companyForm.country);
    setVendorCities([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthReady, canAccessVendors, companyForm.country]);

  useEffect(() => {
    if (!isAuthReady || !canAccessVendors) return;
    if (!companyForm.country) {
      setVendorCities([]);
      return;
    }

    fetchVendorCities(companyForm.country, companyForm.state);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthReady, canAccessVendors, companyForm.country, companyForm.state]);

  useEffect(() => {
    if (!drawerOpen || !invoicingCountryCode) {
      setInvoicingCompanies([]);
      return;
    }

    fetchInvoicingCompanies(invoicingCountryCode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawerOpen, invoicingCountryCode]);

  useEffect(() => {
    if (!defaultTaxName) return;

    setBillingTaxes((prev) =>
      prev.map((tax) => {
        const selectedTaxName =
          tax.tax_name ||
          billingTaxNames.find((taxName) => toSelectValue(taxName.id) === toSelectValue(tax.billing_tax_name_id))?.name ||
          "";
        const normalizedName = selectedTaxName.trim().toLowerCase();
        const selectedIsVisible = filteredTaxNames.some(
          (taxName) => toSelectValue(taxName.id) === toSelectValue(tax.billing_tax_name_id)
        );

        if (!standardTaxNames.includes(normalizedName) || selectedIsVisible || !selectedTaxName) {
          return tax;
        }

        return {
          ...tax,
          billing_tax_name_id: String(defaultTaxName.id),
          tax_name: defaultTaxName.name,
        };
      })
    );
  }, [billingTaxNames, defaultTaxName, filteredTaxNames]);

  useEffect(() => {
    if (!isAuthReady || !canAccessVendors) return undefined;

    const timer = setTimeout(() => {
      fetchVendors(1);
    }, 450);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthReady, canAccessVendors, filters.start_date, filters.end_date, filters.vendor_id, filters.category_id, filters.vendor_type, filters.search]);

  const updateFilter = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const resetForm = () => {
    setClientCpiRules([]);
    setClientRulesTouched(false);
    setCompanyForm({ ...emptyCompanyForm });
    setInvoicingCompanies([]);
    setBillingTaxes([{ ...emptyBillingTax }]);
    setContacts([{ ...emptyContact }]);
    setSelectedVendor(null);
  };

  const openAddDrawer = () => {
    if (!canCreateVendor) {
      showMessage("You do not have permission to add vendors", "error");
      return;
    }

    resetForm();
    setDrawerMode("add");
    setDrawerOpen(true);
  };

  const mapVendorToForm = (vendor) => {
    setClientRulesTouched(false);
    setClientCpiRules((vendor.client_cpi_rules || []).map((rule) => ({
      client_id: rule.client_id,
      cpi_percentage: rule.cpi_percentage ?? "",
      client: rule.client || { id: rule.client_id, name: `Client #${rule.client_id}`, code: "" },
    })));
    setCompanyForm({
      vendor_name: vendor.name || "",
      vendor_email: vendor.email || "",
      vendor_mobile: vendor.mobile || "",
      company_name: vendor.company_name || "",
      official_website: vendor.official_website || "",
      office_phone_number: vendor.office_phone_number || "",
      billing_setting_id: toSelectValue(vendor.billing_setting_id),
      invoicing_company_id: toSelectValue(vendor.invoicing_company_id),
      billing_currency_id: toSelectValue(vendor.billing_currency_id),
      billing_cycle_days:
        vendor.billing_cycle_days === null || vendor.billing_cycle_days === undefined
          ? ""
          : String(vendor.billing_cycle_days),
      country: vendor.country || "",
      se_zone: Boolean(vendor.se_zone),
      pan_number: vendor.pan_number || "",
      city: vendor.city || "",
      state: vendor.state || "",
      postal_code: vendor.postal_code || "",
      company_address: vendor.company_address || "",
      shipping_address: vendor.shipping_address || "",
      note: vendor.note || "",
      company_logo: null,
      existing_company_logo: vendor.company_logo || "",
      complete_redirect_url: vendor.complete_redirect_url || "",
      terminate_redirect_url: vendor.terminate_redirect_url || "",
      over_quota_redirect_url: vendor.over_quota_redirect_url || "",
      quality_terminate_redirect_url: vendor.quality_terminate_redirect_url || "",
      vendor_category_id: toSelectValue(vendor.vendor_category_id),
      vendor_sub_category_id: toSelectValue(vendor.vendor_sub_category_id),
      status: vendor.status || "Active",
      vendor_type: String(vendor.vendor_type || "external").toLowerCase() === "internal" ? "internal" : "external",
    });

    const mappedBillingTaxes =
      Array.isArray(vendor.billing_taxes) && vendor.billing_taxes.length > 0
        ? vendor.billing_taxes.map((tax) => ({
          ...emptyBillingTax,
          id: tax.id,
          billing_tax_name_id: toSelectValue(tax.billing_tax_name_id),
          tax_name: tax.tax_name || tax.billing_tax_name?.name || "",
          tax_number: tax.tax_number || "",
        }))
        : [{ ...emptyBillingTax }];

    setBillingTaxes(mappedBillingTaxes);

    const vendorContacts =
      Array.isArray(vendor.contacts) && vendor.contacts.length > 0
        ? vendor.contacts.map((contact) => ({
          ...emptyContact,
          id: contact.id,
          name: contact.name || "",
          email: contact.email || "",
          login_email: contact.login_email || "",
          profile_picture: null,
          existing_profile_picture: contact.profile_picture || "",
          mobile: contact.mobile || "",
          login_allowed: Boolean(contact.login_allowed),
          status: contact.status || "Active",
          receive_email_notifications: Boolean(contact.receive_email_notifications),
          access_role_ids: Array.isArray(contact.access_role_ids)
            ? contact.access_role_ids.map((roleId) => String(roleId))
            : Array.isArray(contact.access_roles)
              ? contact.access_roles.map((role) => String(role.id)).filter(Boolean)
              : [],
        }))
        : [
          {
            ...emptyContact,
            name: vendor.name || "",
            email: vendor.email || "",
            mobile: vendor.mobile || "",
            login_allowed: false,
            status: vendor.status || "Active",
            receive_email_notifications: false,
          },
        ];

    setContacts(vendorContacts);
  };

  const openVendorDrawer = async (mode, vendor) => {
    closeActionsMenu();

    if (!vendor?.id) return;
    if (mode === "edit" && !canUpdateVendor) {
      showMessage("You do not have permission to edit vendors", "error");
      return;
    }

    try {
      setSubmitting(true);
      const freshVendor = await fetchVendorDetails(vendor.id);

      if (!freshVendor) {
        showMessage("Vendor details not found", "error");
        return;
      }

      setSelectedVendor(freshVendor);
      mapVendorToForm(freshVendor);
      setDrawerMode(mode);
      setDrawerOpen(true);
    } catch (error) {
      showMessage(error.response?.data?.message || "Failed to load vendor details", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const closeDrawerNow = () => {
    setDrawerOpen(false);
    resetForm();
  };

  const closeDrawer = () => {
    if (submitting) return;
    closeDrawerNow();
  };

  const updateCompanyForm = (field, value) => {
    setCompanyForm((prev) => {
      const next = {
        ...prev,
        [field]: value,
        ...(field === "vendor_category_id" ? { vendor_sub_category_id: "" } : {}),
        ...(field === "billing_setting_id" ? { invoicing_company_id: "" } : {}),
        ...(field === "country" ? { state: "", city: "" } : {}),
        ...(field === "state" ? { city: "" } : {}),
      };

      if (field === "company_address") {
        const shouldSyncShipping = !prev.shipping_address || prev.shipping_address === prev.company_address;
        next.shipping_address = shouldSyncShipping ? value : prev.shipping_address;
      }

      return next;
    });
  };

  const updateContact = (index, field, value) => {
    setContacts((prev) =>
      prev.map((contact, contactIndex) =>
        contactIndex === index
          ? {
            ...contact,
            [field]: value,
            ...(field === "login_allowed" && !value ? { login_email: "", password: "", access_role_ids: [] } : {}),
            ...(field === "login_allowed" && value && !contact.login_email ? { login_email: contact.email || "" } : {}),
          }
          : contact
      )
    );
  };

  const addContact = () => {
    if (!canAddContact) {
      showMessage("You do not have permission to add contact rows", "error");
      return;
    }

    setContacts((prev) => [...prev, { ...emptyContact }]);
  };

  const removeContact = (index) => {
    if (!canRemoveContact) {
      showMessage("You do not have permission to remove contact rows", "error");
      return;
    }

    setContacts((prev) => (prev.length === 1 ? prev : prev.filter((_, contactIndex) => contactIndex !== index)));
  };

  const createCategory = async (parentId = null) => {
    if (!canCreateCategory) {
      showMessage("You do not have permission to add this category", "error");
      return;
    }

    const { value: name } = await Swal.fire({
      title: parentId ? "Enter sub category name" : "Enter category name",
      input: "text",
      showCancelButton: true,
      confirmButtonText: "Add",
      cancelButtonText: "Cancel",
      buttonsStyling: false,
      customClass: {
        popup: "custom-swal-popup",
        title: "custom-swal-title",
        input: "custom-swal-input",
        actions: "custom-swal-actions",
        confirmButton: "custom-swal-confirm-btn",
        cancelButton: "custom-swal-cancel-btn",
      },
      inputValidator: (value) => {
        if (!value || !value.trim()) {
          return "You need to write something!";
        }
      },
    });

    if (!name || !name.trim()) return;

    try {
      const response = await axios.post(
        `${API_URL}/vendors-categories`,
        { name: name.trim(), parent_id: parentId || null },
        { headers: { ...requestConfig.headers, "Content-Type": "application/json" } }
      );
      const category = response.data?.data;
      await fetchCategories();

      if (category?.id) {
        if (parentId) {
          updateCompanyForm("vendor_sub_category_id", String(category.id));
        } else {
          updateCompanyForm("vendor_category_id", String(category.id));
        }
      }

      showMessage("Category added successfully");
    } catch (error) {
      showMessage(error.response?.data?.message || "Category add failed", "error");
    }
  };

  const updateBillingTax = (index, field, value) => {
    setBillingTaxes((prev) =>
      prev.map((tax, taxIndex) => {
        if (taxIndex !== index) return tax;

        if (field === "billing_tax_name_id") {
          if (!value) {
            return { ...tax, billing_tax_name_id: "", tax_name: "" };
          }

          if (String(value).startsWith("name:")) {
            return { ...tax, billing_tax_name_id: "", tax_name: String(value).replace(/^name:/, "") };
          }

          const selectedTax = billingTaxNames.find((item) => toSelectValue(item.id) === toSelectValue(value));
          return { ...tax, billing_tax_name_id: value, tax_name: selectedTax?.name || tax.tax_name };
        }

        return { ...tax, [field]: value };
      })
    );
  };

  const addBillingTax = () => {
    if (!canAddTaxRow) {
      showMessage("You do not have permission to add tax rows", "error");
      return;
    }

    setBillingTaxes((prev) => [
      ...prev,
      {
        ...emptyBillingTax,
        billing_tax_name_id: defaultTaxName ? String(defaultTaxName.id) : "",
        tax_name: defaultTaxName?.name || "",
      },
    ]);
  };

  const removeBillingTax = (index) => {
    if (!canRemoveTaxRow) {
      showMessage("You do not have permission to remove tax rows", "error");
      return;
    }

    setBillingTaxes((prev) => (prev.length === 1 ? prev : prev.filter((_, taxIndex) => taxIndex !== index)));
  };

  const createBillingTaxName = async (rowIndex) => {
    if (!canCreateBillingTaxName) {
      showMessage("You do not have permission to add tax names", "error");
      return;
    }

    const { value: name } = await Swal.fire({
      title: "Enter tax name",
      input: "text",
      showCancelButton: true,
      confirmButtonText: "Add",
      cancelButtonText: "Cancel",
      buttonsStyling: false,
      customClass: {
        popup: "custom-swal-popup",
        title: "custom-swal-title",
        input: "custom-swal-input",
        actions: "custom-swal-actions",
        confirmButton: "custom-swal-confirm-btn",
        cancelButton: "custom-swal-cancel-btn",
      },
      inputValidator: (value) => {
        if (!value || !value.trim()) {
          return "You need to write something!";
        }
      },
    });

    if (!name || !name.trim()) return;

    try {
      const response = await axios.post(
        `${API_URL}/billing-tax-names`,
        { name: name.trim() },
        { headers: { ...requestConfig.headers, "Content-Type": "application/json" } }
      );
      const taxName = response.data?.data;
      await fetchBillingOptions();

      if (taxName?.id) {
        setBillingTaxes((prev) =>
          prev.map((tax, taxIndex) =>
            taxIndex === rowIndex
              ? { ...tax, billing_tax_name_id: String(taxName.id), tax_name: taxName.name || name.trim() }
              : tax
          )
        );
      }

      showMessage("Tax name added successfully");
    } catch (error) {
      showMessage(error.response?.data?.message || "Tax name add failed", "error");
    }
  };

  const createBillingCurrency = async () => {
    if (!canCreateBillingCurrency) {
      showMessage("You do not have permission to add billing currencies", "error");
      return;
    }

    const { value: currency } = await Swal.fire({
      title: "Enter billing currency",
      input: "text",
      inputPlaceholder: "e.g. USD",
      showCancelButton: true,
      confirmButtonText: "Add",
      cancelButtonText: "Cancel",
      buttonsStyling: false,
      customClass: {
        popup: "custom-swal-popup",
        title: "custom-swal-title",
        input: "custom-swal-input",
        actions: "custom-swal-actions",
        confirmButton: "custom-swal-confirm-btn",
        cancelButton: "custom-swal-cancel-btn",
      },
      inputValidator: (value) => {
        if (!value || !value.trim()) {
          return "You need to write something!";
        }
      },
    });

    if (!currency || !currency.trim()) return;

    try {
      const response = await axios.post(
        `${API_URL}/billing-currencies`,
        { name: currency.trim(), code: currency.trim() },
        { headers: { ...requestConfig.headers, "Content-Type": "application/json" } }
      );
      const billingCurrency = response.data?.data;
      await fetchBillingOptions();

      if (billingCurrency?.id) {
        updateCompanyForm("billing_currency_id", String(billingCurrency.id));
      }

      showMessage("Billing currency added successfully");
    } catch (error) {
      showMessage(error.response?.data?.message || "Billing currency add failed", "error");
    }
  };

  const createBillingCycle = async () => {
    if (!canCreateBillingCycle) {
      showMessage("You do not have permission to add billing cycles", "error");
      return;
    }

    const { value: days } = await Swal.fire({
      title: "Enter billing cycle days",
      input: "number",
      inputPlaceholder: "e.g. 30",
      showCancelButton: true,
      confirmButtonText: "Add",
      cancelButtonText: "Cancel",
      buttonsStyling: false,
      customClass: {
        popup: "custom-swal-popup",
        title: "custom-swal-title",
        input: "custom-swal-input",
        actions: "custom-swal-actions",
        confirmButton: "custom-swal-confirm-btn",
        cancelButton: "custom-swal-cancel-btn",
      },
      inputValidator: (value) => {
        if (value === "" || value === null || value === undefined) {
          return "You need to write something!";
        }

        if (!/^\d{1,3}$/.test(String(value))) {
          return "Billing cycle should be max 3 digit days.";
        }
      },
    });

    if (days === "" || days === null || days === undefined) return;

    try {
      const response = await axios.post(
        `${API_URL}/billing-cycles`,
        { days: Number(days) },
        { headers: { ...requestConfig.headers, "Content-Type": "application/json" } }
      );
      const billingCycle = response.data?.data;
      await fetchBillingOptions();

      if (billingCycle?.days !== undefined) {
        updateCompanyForm("billing_cycle_days", String(billingCycle.days));
      }

      showMessage("Billing cycle added successfully");
    } catch (error) {
      showMessage(error.response?.data?.message || "Billing cycle add failed", "error");
    }
  };

  const createVendorState = async () => {
    if (!canCreateState) {
      showMessage("You do not have permission to add vendor states", "error");
      return;
    }

    if (!companyForm.country) {
      showMessage("Please select vendor country first", "error");
      return;
    }

    const { value: name } = await Swal.fire({
      title: "Enter state",
      input: "text",
      showCancelButton: true,
      confirmButtonText: "Add",
      cancelButtonText: "Cancel",
      buttonsStyling: false,
      customClass: {
        popup: "custom-swal-popup",
        title: "custom-swal-title",
        input: "custom-swal-input",
        actions: "custom-swal-actions",
        confirmButton: "custom-swal-confirm-btn",
        cancelButton: "custom-swal-cancel-btn",
      },
      inputValidator: (value) => {
        if (!value || !value.trim()) {
          return "You need to write something!";
        }
      },
    });

    if (!name || !name.trim()) return;

    try {
      const response = await axios.post(
        `${API_URL}/vendor-states`,
        {
          name: name.trim(),
          vendor_country_id: selectedVendorCountry?.id,
          country_name: companyForm.country,
        },
        { headers: { ...requestConfig.headers, "Content-Type": "application/json" } }
      );
      const state = response.data?.data;
      await fetchVendorStates(companyForm.country);

      if (state?.name) {
        updateCompanyForm("state", state.name);
      }

      showMessage("Vendor state added successfully");
    } catch (error) {
      showMessage(error.response?.data?.message || "Vendor state add failed", "error");
    }
  };

  const createVendorCity = async () => {
    if (!canCreateCity) {
      showMessage("You do not have permission to add vendor cities", "error");
      return;
    }

    if (!companyForm.country) {
      showMessage("Please select vendor country first", "error");
      return;
    }

    const { value: name } = await Swal.fire({
      title: "Enter city",
      input: "text",
      showCancelButton: true,
      confirmButtonText: "Add",
      cancelButtonText: "Cancel",
      buttonsStyling: false,
      customClass: {
        popup: "custom-swal-popup",
        title: "custom-swal-title",
        input: "custom-swal-input",
        actions: "custom-swal-actions",
        confirmButton: "custom-swal-confirm-btn",
        cancelButton: "custom-swal-cancel-btn",
      },
      inputValidator: (value) => {
        if (!value || !value.trim()) {
          return "You need to write something!";
        }
      },
    });

    if (!name || !name.trim()) return;

    try {
      const response = await axios.post(
        `${API_URL}/vendor-cities`,
        {
          name: name.trim(),
          vendor_country_id: selectedVendorCountry?.id,
          vendor_state_id: selectedVendorState?.id,
          country_name: companyForm.country,
          state_name: companyForm.state,
        },
        { headers: { ...requestConfig.headers, "Content-Type": "application/json" } }
      );
      const city = response.data?.data;
      await fetchVendorCities(companyForm.country, companyForm.state);

      if (city?.name) {
        updateCompanyForm("city", city.name);
      }

      showMessage("Vendor city added successfully");
    } catch (error) {
      showMessage(error.response?.data?.message || "Vendor city add failed", "error");
    }
  };

  const appendPayload = (payload, key, value) => {
    if (value === null || value === undefined) return;
    payload.append(key, value);
  };

  const buildPayload = () => {
    const payload = new FormData();

    const sanitizedContacts = contacts.map((contact, index) => {
      const uploadKey = `contact_profile_picture_${index}`;
      if (contact.profile_picture) {
        payload.append(uploadKey, contact.profile_picture);
      }

      return {
        id: contact.id || undefined,
        name: contact.name,
        email: contact.email,
        login_email: contact.login_allowed ? contact.login_email : "",
        password: contact.login_allowed ? contact.password : "",
        mobile: contact.mobile,
        login_allowed: contact.login_allowed,
        status: contact.status || "Active",
        receive_email_notifications: contact.receive_email_notifications,
        ...(canAssignVendorRoles
          ? {
              access_role_ids: contact.login_allowed
                ? (contact.access_role_ids || []).map((roleId) => String(roleId)).filter(Boolean)
                : [],
            }
          : {}),
        profile_picture_upload_key: contact.profile_picture ? uploadKey : undefined,
      };
    });

    const fields = {
      name: companyForm.vendor_name,
      email: companyForm.vendor_email,
      mobile: companyForm.vendor_mobile,
      country: companyForm.country,
      country_id: selectedVendorCountry?.id || selectedVendor?.country_id || null,
      company_name: companyForm.company_name,
      official_website: companyForm.official_website,
      pan_number: companyForm.pan_number,
      office_phone_number: companyForm.office_phone_number,
      billing_setting_id: companyForm.billing_setting_id,
      ...(canViewVendorField("invoicing_company")
        ? { invoicing_company_id: companyForm.invoicing_company_id }
        : {}),
      billing_currency_id: companyForm.billing_currency_id,
      billing_cycle_days: companyForm.billing_cycle_days,
      ...(canViewVendorField("se_zone")
        ? { se_zone: showVendorSeZone && companyForm.se_zone ? "1" : "0" }
        : {}),
      city: companyForm.city,
      state: companyForm.state,
      state_id: selectedVendorState?.id || selectedVendor?.state_id || null,
      postal_code: companyForm.postal_code,
      company_address: companyForm.company_address,
      shipping_address: companyForm.shipping_address,
      note: companyForm.note,
      complete_redirect_url: companyForm.complete_redirect_url,
      terminate_redirect_url: companyForm.terminate_redirect_url,
      over_quota_redirect_url: companyForm.over_quota_redirect_url,
      quality_terminate_redirect_url: companyForm.quality_terminate_redirect_url,
      vendor_category_id: companyForm.vendor_category_id,
      vendor_sub_category_id: companyForm.vendor_sub_category_id,
      status: companyForm.status || "Active",
      vendor_type: companyForm.vendor_type || "external",
      contacts: JSON.stringify(sanitizedContacts),
      ...(companyForm.vendor_type === "external" &&
        (drawerMode === "add" || clientRulesTouched || selectedVendor?.external_client_rules_configured)
        ? { client_cpi_rules: JSON.stringify(clientCpiRules.map(({ client_id, cpi_percentage }) => ({ client_id, cpi_percentage }))) }
        : {}),
    };

    Object.entries(fields).forEach(([key, value]) => appendPayload(payload, key, value));

    if (companyForm.company_logo) {
      payload.append("company_logo", companyForm.company_logo);
    }

    return payload;
  };

  const validateForm = () => {
    if (!companyForm.vendor_name?.trim()) {
      showMessage("Vendor Name is required", "error");
      return false;
    }

    const vendorEmails = String(companyForm.vendor_email || "")
      .split(",")
      .map((email) => email.trim())
      .filter(Boolean);

    if (vendorEmails.some((email) => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))) {
      showMessage("Please enter valid comma separated Vendor emails", "error");
      return false;
    }

    if (
      companyForm.billing_cycle_days &&
      (!/^\d{1,3}$/.test(String(companyForm.billing_cycle_days)) ||
        Number(companyForm.billing_cycle_days) > 999)
    ) {
      showMessage("Billing Cycle should be max 3 digit days", "error");
      return false;
    }

    if (!["internal", "external"].includes(String(companyForm.vendor_type || "").toLowerCase())) {
      showMessage("Vendor Type is required", "error");
      return false;
    }

    if (companyForm.vendor_type === "external" && clientCpiRules.some((rule) => {
      const value = String(rule.cpi_percentage ?? "").trim();
      return !/^\d+(?:\.\d{1,2})?$/.test(value) || Number(value) < 0 || Number(value) > 100;
    })) {
      showMessage("Enter a Vendor CPI percentage from 0 to 100 (maximum 2 decimals) for every selected client", "error");
      return false;
    }

    for (const contact of contacts) {
      if (contact.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contact.email)) {
        showMessage("Please enter a valid contact email", "error");
        return false;
      }

      if (contact.login_allowed && !contact.login_email?.trim()) {
        showMessage("Login Email is required when login is allowed", "error");
        return false;
      }

      if (contact.login_allowed && canAssignVendorRoles && (contact.access_role_ids || []).length === 0) {
        showMessage("Access Role is required when login is allowed", "error");
        return false;
      }

      if (contact.login_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contact.login_email)) {
        showMessage("Please enter a valid login email", "error");
        return false;
      }

      if (contact.login_allowed && !contact.id && !contact.password) {
        showMessage("Password is required when login is allowed", "error");
        return false;
      }

      if (contact.password && contact.password.length < 8) {
        showMessage("Contact password must have at least 8 characters", "error");
        return false;
      }
    }

    return true;
  };

  const handleSubmit = async () => {
    if (drawerMode === "edit" && !canUpdateVendor) {
      showMessage("You do not have permission to update vendors", "error");
      return;
    }
    if (drawerMode !== "edit" && !canCreateVendor) {
      showMessage("You do not have permission to add vendors", "error");
      return;
    }

    if (!validateForm()) return;

    const payload = buildPayload();

    try {
      setSubmitting(true);

      if (drawerMode === "edit" && selectedVendor?.id) {
        payload.append("_method", "PUT");
        await axios.post(`${API_URL}/vendors/${selectedVendor.id}`, payload, {
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
        });
        showMessage("Vendor updated successfully");
      } else {
        await axios.post(`${API_URL}/vendors`, payload, {
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
        });
        showMessage("Vendor added successfully");
      }

      closeDrawerNow();
      await fetchVendors(pagination.currentPage);
      await fetchVendorOptions();
    } catch (error) {
      showMessage(error.response?.data?.message || error.message || "Vendor save failed", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const openActionsMenu = (event, vendor) => {
    if (!canUseRowActions) return;

    setMenuAnchor(event.currentTarget);
    setMenuVendor(vendor);
  };

  const closeActionsMenu = () => {
    setMenuAnchor(null);
    setMenuVendor(null);
  };

  const openDeleteDialog = () => {
    if (!canDeleteVendor) {
      showMessage("You do not have permission to delete vendors", "error");
      closeActionsMenu();
      return;
    }

    setSelectedVendor(menuVendor);
    setDeleteDialogOpen(true);
    closeActionsMenu();
  };

  const confirmDelete = async () => {
    if (!selectedVendor?.id) return;
    if (!canDeleteVendor) {
      showMessage("You do not have permission to delete vendors", "error");
      return;
    }

    try {
      setSubmitting(true);
      await axios.delete(`${API_URL}/vendors/${selectedVendor.id}`, requestConfig);
      showMessage("Vendor deleted successfully");
      setDeleteDialogOpen(false);
      setSelectedVendor(null);
      await fetchVendors(pagination.currentPage);
      await fetchVendorOptions();
    } catch (error) {
      showMessage(error.response?.data?.message || "Vendor delete failed", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const goToPage = (page) => {
    const nextPage = Math.max(1, Math.min(page, pagination.lastPage || 1));
    fetchVendors(nextPage);
  };

  const renderField = (label, node) => (
    <SoftBox>
      <SoftTypography variant="body2" sx={formLabelSx}>
        {label}
      </SoftTypography>
      {node}
    </SoftBox>
  );

  const makeSelectOption = (value, label) => ({
    value: String(value),
    label: label || String(value),
  });

  const getSelectedOption = (options, value) =>
    options.find((option) => String(option.value) === String(value)) || null;

  const getSelectedOptions = (options, values = []) => {
    const selected = new Set((Array.isArray(values) ? values : []).map((value) => String(value)));
    return options.filter((option) => selected.has(String(option.value)));
  };

  const renderFormSelect = ({
    value,
    options,
    onChange,
    placeholder = "--",
    disabled = readOnly,
    isClearable = true,
    isSearchable = true,
    formatOptionLabel,
  }) => (
    <SoftSelect
      size="small"
      placeholder={placeholder}
      value={getSelectedOption(options, value)}
      options={options}
      onChange={(option) => onChange(option?.value || "")}
      isDisabled={disabled}
      isClearable={isClearable}
      isSearchable={isSearchable}
      formatOptionLabel={formatOptionLabel}
      sx={formSelectSx}
    />
  );

  const VendorFieldGrid = useMemo(() => {
    const FieldGrid = ({ field, children, ...gridProps }) =>
      canViewVendorField(field) ? (
        <Grid item {...gridProps}>
          {children}
        </Grid>
      ) : null;

    FieldGrid.propTypes = {
      field: PropTypes.string.isRequired,
      children: PropTypes.node.isRequired,
    };

    return FieldGrid;
  }, [canViewVendorField]);

  const ContactFieldGrid = useMemo(() => {
    const FieldGrid = ({ field, children, ...gridProps }) =>
      canViewContactField(field) ? (
        <Grid item {...gridProps}>
          {children}
        </Grid>
      ) : null;

    FieldGrid.propTypes = {
      field: PropTypes.string.isRequired,
      children: PropTypes.node.isRequired,
    };

    return FieldGrid;
  }, [canViewContactField]);

  const renderBillingTaxBlock = (tax, index) => {
    const selectedTaxValue = tax.billing_tax_name_id || (tax.tax_name ? `name:${tax.tax_name}` : "");
    const taxNameOptions = [
      ...(tax.tax_name && !tax.billing_tax_name_id ? [makeSelectOption(`name:${tax.tax_name}`, tax.tax_name)] : []),
      ...filteredTaxNames.map((taxName) => makeSelectOption(taxName.id, taxName.name)),
    ];

    return (
      <React.Fragment key={`billing-tax-${index}`}>
        <VendorFieldGrid field="tax_name" xs={12} md={4} xl={3}>
          <SoftTypography variant="body2" sx={formLabelSx}>
            Tax Name
          </SoftTypography>
          <Stack direction="row" spacing={1} sx={fieldWithActionSx}>
            {renderFormSelect({
              value: selectedTaxValue,
              options: taxNameOptions,
              onChange: (nextValue) => updateBillingTax(index, "billing_tax_name_id", nextValue),
            })}
            {!readOnly && canCreateBillingTaxName && (
              <Tooltip title="Add tax name">
                <IconButton color="info" onClick={() => createBillingTaxName(index)} sx={fieldActionButtonSx}>
                  <AddIcon />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </VendorFieldGrid>
        <VendorFieldGrid field="tax_number" xs={12} md={4} xl={3}>
          {renderField(
            taxNumberLabel,
            <SoftBox sx={fieldWithActionSx}>
              <SoftInput
                placeholder={taxNumberLabel}
                value={tax.tax_number}
                disabled={readOnly}
                onChange={(event) => updateBillingTax(index, "tax_number", event.target.value)}
              />
              {!readOnly && (canAddTaxRow || (billingTaxes.length > 1 && canRemoveTaxRow)) && (
                <SoftBox sx={billingTaxes.length > 1 ? fieldActionsSx : fieldActionButtonSx}>
                  {canAddTaxRow && (
                    <Tooltip title="Add tax row">
                      <IconButton color="info" onClick={addBillingTax} sx={fieldActionIconSx}>
                        <AddIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                  {billingTaxes.length > 1 && canRemoveTaxRow && (
                    <Tooltip title="Remove tax row">
                      <IconButton color="error" onClick={() => removeBillingTax(index)} sx={fieldActionIconSx}>
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                </SoftBox>
              )}
            </SoftBox>
          )}
        </VendorFieldGrid>
      </React.Fragment>
    );
  };

  const renderContactBlock = (contact, index) => (
    <SoftBox key={`contact-${index}`} sx={sectionSx}>
      <SoftBox display="flex" justifyContent="space-between" alignItems="center" mb={0.375}>
        <SoftTypography variant="h6" sx={{ fontSize: "1rem" }}>
          Contact Details {contacts.length > 1 ? index + 1 : ""}
        </SoftTypography>
        {contacts.length > 1 && !readOnly && canRemoveContact && (
          <Tooltip title="Remove contact">
            <IconButton size="small" color="error" onClick={() => removeContact(index)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </SoftBox>

      <Grid container rowSpacing={0.5} columnSpacing={2} sx={{ width: "100%", ml: 0 }}>
        <ContactFieldGrid field="name" xs={12} md={4} xl={3}>
          {renderField(
            "Contact Name",
            <SoftInput
              placeholder="e.g. John Doe"
              value={contact.name}
              disabled={readOnly}
              onChange={(event) => updateContact(index, "name", event.target.value)}
            />
          )}
        </ContactFieldGrid>
        <ContactFieldGrid field="email" xs={12} md={4} xl={3}>
          {renderField(
            "Contact Email",
            <SoftInput
              name={`vendor_contact_email_${index}`}
              autoComplete="new-password"
              placeholder="e.g. contact@example.com"
              value={contact.email}
              disabled={readOnly}
              onChange={(event) => updateContact(index, "email", event.target.value)}
            />
          )}
        </ContactFieldGrid>
        <ContactFieldGrid field="mobile" xs={12} md={4} xl={3}>
          {renderField(
            "Mobile",
            <SoftInput
              placeholder="e.g. 1234567890"
              value={contact.mobile}
              disabled={readOnly}
              onChange={(event) => updateContact(index, "mobile", event.target.value)}
            />
          )}
        </ContactFieldGrid>
        <ContactFieldGrid field="profile_picture" xs={12} md={4} xl={3}>
          {renderField(
            "Profile Picture",
            <SoftBox
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              sx={{
                border: "1px dashed #cbd5e1",
                borderRadius: "6px",
                p: 0.5,
                minHeight: 32,
                backgroundColor: "#f8fafc",
              }}
            >
              <SoftTypography variant="caption" color="text" sx={{ ml: 0.75, fontSize: "11px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {contact.profile_picture?.name || (contact.existing_profile_picture ? "Current image saved" : "No file chosen")}
              </SoftTypography>
              <SoftButton
                variant="outlined"
                color="info"
                size="small"
                component="label"
                disabled={readOnly}
                sx={{ ...fileButtonSx, mb: 0 }}
              >
                Choose File
                <input
                  type="file"
                  accept="image/*"
                  hidden
                  disabled={readOnly}
                  onChange={(event) => updateContact(index, "profile_picture", event.target.files?.[0] || null)}
                />
              </SoftButton>
            </SoftBox>
          )}
        </ContactFieldGrid>
        <ContactFieldGrid field="status" xs={12} md={4} xl={3}>
          {renderField(
            "Contact Status",
            renderFormSelect({
              value: contact.status || "Active",
              options: bulkStatusOptions,
              onChange: (nextValue) => updateContact(index, "status", nextValue || "Active"),
              isClearable: false,
              isSearchable: false,
            })
          )}
        </ContactFieldGrid>
        <ContactFieldGrid field="receive_email_notifications" xs={12} md={4} xl={3}>
          {renderField(
            "Receive Email Notifications?",
            <RadioGroup
              row
              value={contact.receive_email_notifications ? "yes" : "no"}
              onChange={(event) =>
                updateContact(index, "receive_email_notifications", event.target.value === "yes")
              }
            >
              <FormControlLabel value="yes" control={<Radio disabled={readOnly} />} label="Yes" />
              <FormControlLabel value="no" control={<Radio disabled={readOnly} />} label="No" />
            </RadioGroup>
          )}
        </ContactFieldGrid>
        {contact.login_allowed && canViewContactField("login_email") && (
          <>
            <ContactFieldGrid field="login_email" xs={12} md={4} xl={3}>
              {renderField(
                "Login Email",
                <SoftInput
                  name={`vendor_contact_login_email_${index}`}
                  autoComplete="new-password"
                  placeholder="e.g. login@example.com"
                  value={contact.login_email}
                  disabled={readOnly}
                  onChange={(event) => updateContact(index, "login_email", event.target.value)}
                />
              )}
            </ContactFieldGrid>
            <ContactFieldGrid field="password" xs={12} md={4} xl={3}>
              {renderField(
                "Password",
                <SoftInput
                  name={`vendor_contact_password_${index}`}
                  type="password"
                  autoComplete="new-password"
                  placeholder={drawerMode === "edit" ? "Leave blank to keep old password" : "Must have at least 8 characters"}
                  value={contact.password}
                  disabled={readOnly}
                  onChange={(event) => updateContact(index, "password", event.target.value)}
                />
              )}
            </ContactFieldGrid>
            {canViewContactAccessRoles && (
              <ContactFieldGrid field="access_roles" xs={12} md={4} xl={3}>
                {renderField(
                  "Access Role *",
                  <SoftSelect
                    size="small"
                    placeholder="Select role"
                    value={getSelectedOptions(vendorAccessRoleOptions, contact.access_role_ids)}
                    options={vendorAccessRoleOptions}
                    onChange={(selected) =>
                      updateContact(
                        index,
                        "access_role_ids",
                        Array.isArray(selected) ? selected.map((option) => String(option.value)) : []
                      )
                    }
                    isDisabled={readOnly || !canAssignVendorRoles}
                    isClearable
                    isSearchable
                    isMulti
                    closeMenuOnSelect={false}
                    sx={formSelectSx}
                  />
                )}
              </ContactFieldGrid>
            )}
          </>
        )}
        <ContactFieldGrid field="login_allowed" xs={12} md={4} xl={3}>
          {renderField(
            "Login Allowed?",
            <RadioGroup
              row
              value={contact.login_allowed ? "yes" : "no"}
              onChange={(event) => updateContact(index, "login_allowed", event.target.value === "yes")}
            >
              <FormControlLabel value="yes" control={<Radio disabled={readOnly} />} label="Yes" />
              <FormControlLabel value="no" control={<Radio disabled={readOnly} />} label="No" />
            </RadioGroup>
          )}
        </ContactFieldGrid>
      </Grid>
    </SoftBox>
  );

  const getAvatarUrl = (path) => {
    if (!path || typeof path !== "string") return null;
    // If it's already a full URL or protocol-relative, use as-is
    if (path.startsWith("http") || path.startsWith("//")) return path;

    // Ensure base API URL doesn't end with /api
    const base = API_URL.replace(/\/api\/?$/i, "");

    // Normalize path (remove leading slashes)
    let p = path.replace(/^\/+/, "");

    // If backend already returned a path starting with 'storage/', don't add another
    if (!p.startsWith("storage/")) {
      p = `storage/${p}`;
    }

    return `${base}/${p}`;
  };

  if (!isAuthReady) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <SoftBox display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <FlagLoader />
        </SoftBox>
      </DashboardLayout>
    );
  }

  if (!canAccessVendors) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <SoftBox p={3}>
          <Card sx={{ p: 3, borderRadius: "8px" }}>
            <SoftTypography variant="h5" fontWeight="bold">
              Vendors
            </SoftTypography>
            <SoftTypography variant="body2" color="text" mt={1}>
              You do not have permission to view vendors.
            </SoftTypography>
          </Card>
        </SoftBox>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <style>{customSwalStyle}</style>
      <DashboardNavbar />

      <SoftSnackbar
        color={snackbar.color}
        icon="notifications"
        title="Vendors"
        content={snackbar.message}
        open={snackbar.open}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        close={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        time={3000}
      />

      <SoftBox p={3}>
        <SoftBox display="flex" justifyContent="space-between" alignItems="center" mb={1} gap={1}>
          <SoftTypography variant="h4" fontWeight="bold" sx={{ fontSize: "0.875rem" }}>
            Vendors
          </SoftTypography>
          {canCreateVendor && (
            <SoftButton color="info" size="small" sx={listExportButtonSx} startIcon={<AddIcon />} onClick={openAddDrawer}>
              Add Vendor
            </SoftButton>
          )}
        </SoftBox>

        <Card sx={{ borderRadius: "8px", overflow: "hidden", boxShadow: "0 12px 28px rgba(15, 23, 42, 0.06)" }}>
          <SoftBox
            display="flex"
            justifyContent="space-between"
            alignItems={{ xs: "stretch", sm: "center" }}
            flexDirection={{ xs: "column", sm: "row" }}
            gap={0.75}
            px="2px"
            py="2px"
            sx={{ borderBottom: "1px solid #e2e8f0" }}
          >
            <SoftBox color="text" fontSize="12px" fontWeight={400}>
              Showing {pagination.from} to {pagination.to} of {pagination.total} entries
            </SoftBox>
            <SoftBox
              display="flex"
              flexWrap="wrap"
              gap={0.75}
              justifyContent={{ xs: "flex-start", sm: "flex-end" }}
              sx={{ ml: { sm: "auto" }, width: { xs: "100%", sm: "auto" } }}
            >
              <SoftBox sx={listSearchControlSx}>
                <SoftInput
                  size="small"
                  placeholder="Search..."
                  value={filters.search}
                  onChange={(event) => updateFilter("search", event.target.value)}
                  sx={listSearchInputSx}
                />
              </SoftBox>
              {showCreatedColumn && (
                <>
                  <SoftBox sx={listSearchControlSx}>
                    <SoftInput
                      size="small"
                      type="date"
                      value={filters.start_date}
                      onChange={(event) => updateFilter("start_date", event.target.value)}
                      sx={listSearchInputSx}
                      inputProps={{ "aria-label": "Start Date" }}
                    />
                  </SoftBox>
                  <SoftBox sx={listSearchControlSx}>
                    <SoftInput
                      size="small"
                      type="date"
                      value={filters.end_date}
                      onChange={(event) => updateFilter("end_date", event.target.value)}
                      sx={listSearchInputSx}
                      inputProps={{ "aria-label": "End Date" }}
                    />
                  </SoftBox>
                </>
              )}
              {showNameColumn && (
                <SoftBox sx={listSearchControlSx}>
                  <SoftSelect
                    size="small"
                    placeholder="Vendor"
                    value={selectedVendorFilter}
                    options={vendorFilterOptions}
                    onChange={(options) => updateFilter("vendor_id", (options || []).map((option) => option.value))}
                    isSearchable
                    isClearable
                    isMulti
                    sx={listSearchSelectSx}
                  />
                </SoftBox>
              )}
              {showCategoryColumn && (
                <SoftBox sx={listSearchControlSx}>
                  <SoftSelect
                    size="small"
                    placeholder="Category"
                    value={selectedCategoryFilter}
                    options={categoryFilterOptions}
                    onChange={(options) => updateFilter("category_id", (options || []).map((option) => option.value))}
                    isSearchable
                    isClearable
                    isMulti
                    sx={listSearchSelectSx}
                  />
                </SoftBox>
              )}
              {showVendorTypeColumn && (
                <SoftBox sx={listSearchControlSx}>
                  <SoftSelect
                    size="small"
                    placeholder="Type"
                    value={selectedVendorTypeFilter}
                    options={vendorTypeOptions}
                    onChange={(options) => updateFilter("vendor_type", (options || []).map((option) => option.value))}
                    isSearchable
                    isClearable
                    isMulti
                    sx={listSearchSelectSx}
                  />
                </SoftBox>
              )}
              {canExportVendors && (
                <SoftButton
                  variant="outlined"
                  color="info"
                  size="small"
                  sx={listExportButtonSx}
                  onClick={handleExport}
                  disabled={loading || isExporting}
                >
                  <DownloadIcon sx={{ mr: 0.5, fontSize: "14px !important" }} />
                  {isExporting ? "Exporting..." : "Export"}
                </SoftButton>
              )}
            </SoftBox>
          </SoftBox>

          <SoftBox display="flex" justifyContent="space-between" alignItems="center" px={1} py={0.75} sx={{ borderBottom: "1px solid #e2e8f0", flexWrap: "wrap", gap: 0.75 }}>
            <SoftBox display="flex" flexWrap="wrap" gap={0.75}>
              {canUseBulkActions && (
                <>
                  <SoftBox sx={bulkControlSx}>
                    <SoftSelect
                      size="small"
                      placeholder="No Action"
                      value={selectedBulkAction}
                      options={bulkActionOptions}
                      onChange={(option) => setBulkAction(option?.value || "")}
                      isClearable
                      sx={listSearchSelectSx}
                    />
                  </SoftBox>
                  {bulkAction === "change_status" && (
                    <SoftBox sx={bulkControlSx}>
                      <SoftSelect
                        size="small"
                        placeholder="Status"
                        value={selectedBulkStatus}
                        options={bulkStatusOptions}
                        onChange={(option) => setBulkStatus(option?.value || "Active")}
                        sx={listSearchSelectSx}
                      />
                    </SoftBox>
                  )}
                  <SoftButton color="info" size="small" sx={bulkButtonSx} onClick={handleApplyBulkAction} disabled={loading || !bulkAction}>
                    Apply
                  </SoftButton>
                </>
              )}
            </SoftBox>
            <Stack direction="row" spacing={1}>
              <SoftButton
                size="small"
                variant="outlined"
                color="dark"
                sx={toolbarButtonSx}
                disabled={pagination.currentPage <= 1 || loading}
                onClick={() => goToPage(pagination.currentPage - 1)}
              >
                Previous
              </SoftButton>
              <SoftButton
                size="small"
                variant="outlined"
                color="dark"
                sx={toolbarButtonSx}
                disabled={pagination.currentPage >= pagination.lastPage || loading}
                onClick={() => goToPage(pagination.currentPage + 1)}
              >
                Next
              </SoftButton>
            </Stack>
          </SoftBox>

          <TableContainer component={Paper} sx={{ boxShadow: "none", overflowX: "auto" }}>
            <Table sx={{ minWidth: 1160 }}>
              <TableHead sx={{ display: "table-header-group" }}>
                <TableRow>
                  {canUseBulkActions && (
                    <TableCell sx={headerCellSx} padding="checkbox">
                      <Checkbox
                        size="small"
                        checked={vendors.length > 0 && selectedVendors.length === vendors.length}
                        indeterminate={selectedVendors.length > 0 && selectedVendors.length < vendors.length}
                        onChange={handleSelectAll}
                      />
                    </TableCell>
                  )}
                  {showNameColumn && <TableCell sx={headerCellSx}>Name</TableCell>}
                  {showEmailColumn && <TableCell sx={headerCellSx}>Email</TableCell>}
                  {showMobileColumn && <TableCell sx={headerCellSx}>Mobile</TableCell>}
                  {showCategoryColumn && <TableCell sx={headerCellSx}>Category</TableCell>}
                  {showVendorTypeColumn && <TableCell sx={headerCellSx}>Type</TableCell>}
                  {showStatusColumn && <TableCell sx={headerCellSx}>Status</TableCell>}
                  {showCreatedColumn && <TableCell sx={headerCellSx}>Created</TableCell>}
                  {canUseRowActions && (
                    <TableCell sx={headerCellSx} align="center">
                      Action
                    </TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={visibleTableColumnCount} sx={bodyCellSx}>
                      <SoftBox display="flex" justifyContent="center" alignItems="center" minHeight={280}>
                        <FlagLoader />
                      </SoftBox>
                    </TableCell>
                  </TableRow>
                ) : vendors.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={visibleTableColumnCount} sx={bodyCellSx}>
                      <SoftBox py={5} textAlign="center">
                        <SoftTypography variant="h6" color="text">
                          No vendors found
                        </SoftTypography>
                      </SoftBox>
                    </TableCell>
                  </TableRow>
                ) : (
                  vendors.map((vendor) => {
                    const displayName = vendor.name || "-";
                    const companyName = vendor.company_name || "";
                    const displayEmail = vendor.email || "-";
                    const displayMobile = vendor.mobile || "--";
                    const status = vendor.status || "Active";
                    const isActive = String(status).toLowerCase() === "active";
                    const vendorType = formatVendorType(vendor.vendor_type);
                    const isInternalVendor = vendorType === "Internal";
                    const avatarPath = vendor.company_logo;

                    return (
                      <TableRow key={vendor.id} hover sx={{ "&:hover": { backgroundColor: "#f8fafc" } }}>
                        {canUseBulkActions && (
                          <TableCell sx={bodyCellSx} padding="checkbox">
                            <Checkbox
                              size="small"
                              checked={selectedVendors.includes(vendor.id)}
                              onChange={() => handleSelectRow(vendor.id)}
                            />
                          </TableCell>
                        )}
                        {showNameColumn && (
                          <TableCell sx={bodyCellSx}>
                            <SoftBox
                              display="flex"
                              alignItems="center"
                              onClick={() => canOpenVendorPage && navigate(`/vendors/view/${vendor.id}`)}
                              sx={{ cursor: canOpenVendorPage ? "pointer" : "default" }}
                            >
                              {canViewVendorField("company_logo") && avatarPath && (
                                <SoftBox mr={2}>
                                  <img
                                    src={getAvatarUrl(avatarPath)}
                                    alt="Avatar"
                                    style={{ width: "30px", height: "30px", borderRadius: "50%", objectFit: "cover" }}
                                  />
                                </SoftBox>
                              )}
                              <SoftBox display="flex" flexDirection="column">
                                {canViewVendorField("name") && (
                                  <SoftTypography variant="body2" color="dark" sx={{ fontSize: "10px", fontWeight: 600, lineHeight: 1.15 }}>
                                    {displayName}
                                  </SoftTypography>
                                )}
                                {canViewVendorField("company_name") && companyName && (
                                  <SoftTypography variant="caption" color="text" sx={{ fontSize: "9px", lineHeight: 1.1 }}>
                                    {companyName}
                                  </SoftTypography>
                                )}
                              </SoftBox>
                            </SoftBox>
                          </TableCell>
                        )}
                        {showEmailColumn && <TableCell sx={bodyCellSx}>{displayEmail}</TableCell>}
                        {showMobileColumn && <TableCell sx={bodyCellSx}>{displayMobile}</TableCell>}
                        {showCategoryColumn && <TableCell sx={bodyCellSx}>{vendor.category?.name || "--"}</TableCell>}
                        {showVendorTypeColumn && (
                          <TableCell sx={bodyCellSx}>
                            <Chip
                              label={vendorType}
                              size="small"
                              sx={{
                                height: 22,
                                color: isInternalVendor ? "#075985" : "#475569",
                                backgroundColor: isInternalVendor ? "#e0f2fe" : "#f1f5f9",
                                fontSize: "10px",
                              }}
                            />
                          </TableCell>
                        )}
                        {showStatusColumn && (
                          <TableCell sx={bodyCellSx}>
                            <Chip
                              label={status}
                              size="small"
                              sx={{
                                height: 22,
                                color: isActive ? "#15803d" : "#b91c1c",
                                backgroundColor: isActive ? "#dcfce7" : "#fee2e2",
                                fontSize: "10px",
                              }}
                            />
                          </TableCell>
                        )}
                        {showCreatedColumn && <TableCell sx={bodyCellSx}>{formatDate(vendor.created_at)}</TableCell>}
                        {canUseRowActions && (
                          <TableCell sx={bodyCellSx} align="center">
                            <Tooltip title="Actions">
                              <IconButton size="small" onClick={(event) => openActionsMenu(event, vendor)}>
                                <MoreVertIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      </SoftBox>

      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeActionsMenu}>
        {canOpenVendorPage && (
          <MenuItem onClick={() => { navigate(`/vendors/view/${menuVendor?.id}`); closeActionsMenu(); }}>
            <VisibilityIcon fontSize="small" sx={{ mr: 1 }} />
            View
          </MenuItem>
        )}
        {canUpdateVendor && (
          <MenuItem onClick={() => openVendorDrawer("edit", menuVendor)}>
            <EditIcon fontSize="small" sx={{ mr: 1 }} />
            Edit
          </MenuItem>
        )}
        {canDeleteVendor && (
          <MenuItem onClick={openDeleteDialog}>
            <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
            Delete
          </MenuItem>
        )}
      </Menu>

      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={closeDrawer}
        disableEnforceFocus
        PaperProps={{
          sx: {
            width: {
              xs: "100%",
              md: "calc(100vw - 48px)",
              xl: `calc(100vw - ${drawerDesktopOffset}px)`,
            },
            maxWidth: "100%",
            backgroundColor: "#f8fafc",
          },
        }}
      >
        <SoftBox display="flex" flexDirection="column" height="100%">
          <SoftBox
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            px={1.5}
            py={0.75}
            sx={{ backgroundColor: "#fff", borderBottom: "1px solid #e2e8f0" }}
          >
            <SoftTypography variant="h5" fontWeight="bold" sx={{ fontSize: "1rem" }}>
              {drawerTitle}
            </SoftTypography>
            <IconButton size="small" onClick={closeDrawer}>
              <CloseIcon />
            </IconButton>
          </SoftBox>

          <SoftBox p={0.75} sx={{ overflowY: "auto", flex: 1 }}>
            <SoftBox sx={sectionSx}>
              <SoftTypography variant="h6" sx={{ fontSize: "1rem", mb: 0.5 }}>
                Vendor Details
              </SoftTypography>
              <Grid container rowSpacing={0.5} columnSpacing={2} sx={{ width: "100%", ml: 0 }}>
                <VendorFieldGrid field="name" xs={12} md={4} xl={3}>
                  {renderField(
                    "Vendor Name *",
                    <SoftInput
                      placeholder="e.g. John Doe"
                      value={companyForm.vendor_name}
                      disabled={readOnly}
                      onChange={(event) => updateCompanyForm("vendor_name", event.target.value)}
                    />
                  )}
                </VendorFieldGrid>
                <VendorFieldGrid field="email" xs={12} md={4} xl={3}>
                  {renderField(
                    "Vendor Email",
                    <SoftInput
                      placeholder="john@example.com, billing@example.com"
                      value={companyForm.vendor_email}
                      disabled={readOnly}
                      onChange={(event) => updateCompanyForm("vendor_email", event.target.value)}
                    />
                  )}
                </VendorFieldGrid>
                <VendorFieldGrid field="mobile" xs={12} md={4} xl={3}>
                  {renderField(
                    "Vendor Phone",
                    <SoftInput
                      placeholder="e.g. +1234567890"
                      value={companyForm.vendor_mobile}
                      disabled={readOnly}
                      onChange={(event) => updateCompanyForm("vendor_mobile", event.target.value)}
                    />
                  )}
                </VendorFieldGrid>
                <VendorFieldGrid field="official_website" xs={12} md={4} xl={3}>
                  {renderField(
                    "Official Website",
                    <SoftInput
                      placeholder="https://www.example.com"
                      value={companyForm.official_website}
                      disabled={readOnly}
                      onChange={(event) => updateCompanyForm("official_website", event.target.value)}
                    />
                  )}
                </VendorFieldGrid>
                <VendorFieldGrid field="office_phone_number" xs={12} md={4} xl={3}>
                  {renderField(
                    "Office Phone Number",
                    <SoftInput
                      placeholder="+19876543"
                      value={companyForm.office_phone_number}
                      disabled={readOnly}
                      onChange={(event) => updateCompanyForm("office_phone_number", event.target.value)}
                    />
                  )}
                </VendorFieldGrid>
                <VendorFieldGrid field="status" xs={12} md={4} xl={3}>
                  {renderField(
                    "Status",
                    renderFormSelect({
                      value: companyForm.status || "Active",
                      options: bulkStatusOptions,
                      onChange: (nextValue) => updateCompanyForm("status", nextValue || "Active"),
                      isClearable: false,
                      isSearchable: false,
                    })
                  )}
                </VendorFieldGrid>
                <VendorFieldGrid field="vendor_type" xs={12} md={4} xl={3}>
                  {renderField(
                    "Vendor Type *",
                    renderFormSelect({
                      value: companyForm.vendor_type || "external",
                      options: vendorTypeOptions,
                      onChange: (nextValue) => updateCompanyForm("vendor_type", nextValue || "external"),
                      isClearable: false,
                      isSearchable: false,
                    })
                  )}
                </VendorFieldGrid>
                {companyForm.vendor_type === "external" && (
                  <Grid item xs={12}>
                    <SoftTypography variant="body2" sx={formLabelSx}>Clients / Vendor CPI (%)</SoftTypography>
                    <Autocomplete
                      multiple
                      disableCloseOnSelect
                      options={Array.from(new Map([...clientOptions, ...clientCpiRules.map((rule) => rule.client)]
                        .filter(Boolean).map((client) => [String(client.id), client])).values())}
                      value={clientCpiRules.map((rule) => rule.client)}
                      isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
                      getOptionLabel={(client) => [client.name, client.code ? `(${client.code})` : ""].filter(Boolean).join(" ")}
                      loading={clientOptionsLoading}
                      disabled={readOnly || clientOptionsLoading}
                      onChange={(_, clients) => {
                        setClientRulesTouched(true);
                        setClientCpiRules(clients.map((client) => ({
                          client,
                          client_id: client.id,
                          cpi_percentage: clientCpiRules.find((rule) => String(rule.client_id) === String(client.id))?.cpi_percentage ?? "",
                        })));
                      }}
                      renderOption={(props, option, { selected }) => (
                        <li {...props}>
                          <Checkbox checked={selected} sx={{ mr: 1 }} />
                          {option.name}{option.code ? ` (${option.code})` : ""}
                        </li>
                      )}
                      renderInput={(params) => <TextField {...params} placeholder="Select clients" size="small" />}
                    />
                    <SoftTypography variant="caption" color="text" sx={{ display: "block", mt: 0.5 }}>
                      {selectedVendor && !selectedVendor.external_client_rules_configured && !clientRulesTouched
                        ? "Existing access and saved CPI stay unchanged until you select clients."
                        : "Only selected clients' projects will be available. No selected clients means no project access."}
                    </SoftTypography>
                    <Grid container spacing={1} sx={{ mt: 0.25 }}>
                      {clientCpiRules.map((rule) => (
                        <Grid item xs={12} md={4} key={rule.client_id}>
                          <SoftTypography variant="body2" sx={formLabelSx}>{rule.client.name} — Vendor CPI (%) *</SoftTypography>
                          <SoftInput
                            value={rule.cpi_percentage}
                            disabled={readOnly}
                            placeholder="0 to 100"
                            inputProps={{ inputMode: "decimal" }}
                            onChange={(event) => {
                              const value = event.target.value;
                              if (!/^\d{0,3}(?:\.\d{0,2})?$/.test(value)) return;
                              setClientRulesTouched(true);
                              setClientCpiRules((current) => current.map((entry) =>
                                entry.client_id === rule.client_id ? { ...entry, cpi_percentage: value } : entry));
                            }}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  </Grid>
                )}
                <VendorFieldGrid field="vendor_category" xs={12} md={4} xl={3}>
                  <SoftTypography variant="body2" sx={formLabelSx}>
                    Vendor Category
                  </SoftTypography>
                  <Stack direction="row" spacing={1} sx={fieldWithActionSx}>
                    {renderFormSelect({
                      value: companyForm.vendor_category_id,
                      options: rootCategories.map((category) => makeSelectOption(category.id, category.name)),
                      onChange: (nextValue) => updateCompanyForm("vendor_category_id", nextValue),
                    })}
                    {!readOnly && canCreateCategory && (
                      <Tooltip title="Add category">
                        <IconButton color="info" onClick={() => createCategory(null)} sx={fieldActionButtonSx}>
                          <AddIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Stack>
                </VendorFieldGrid>
                <VendorFieldGrid field="note" xs={12} md={4} xl={3}>
                  {renderField(
                    "Note",
                    <SoftInput
                      placeholder="Add note"
                      value={companyForm.note}
                      disabled={readOnly}
                      onChange={(event) => updateCompanyForm("note", event.target.value)}
                    />
                  )}
                </VendorFieldGrid>
                <VendorFieldGrid field="company_logo" xs={12} md={4} xl={3}>
                  {renderField(
                    "Company Logo",
                    <SoftBox
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      sx={{
                        border: "1px dashed #cbd5e1",
                        borderRadius: "6px",
                        p: 0.5,
                        minHeight: 32,
                        backgroundColor: "#f8fafc",
                      }}
                    >
                      <SoftTypography variant="caption" color="text" sx={{ ml: 0.75, fontSize: "11px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {companyForm.company_logo?.name || (companyForm.existing_company_logo ? "Current logo saved" : "No file chosen")}
                      </SoftTypography>
                      <SoftButton
                        variant="outlined"
                        color="info"
                        size="small"
                        component="label"
                        disabled={readOnly}
                        sx={{ ...fileButtonSx, mb: 0 }}
                      >
                        Choose File
                        <input
                          type="file"
                          accept="image/*"
                          hidden
                          disabled={readOnly}
                          onChange={(event) => updateCompanyForm("company_logo", event.target.files?.[0] || null)}
                        />
                      </SoftButton>
                    </SoftBox>
                  )}
                </VendorFieldGrid>
              </Grid>
            </SoftBox>

            {canViewAnyRedirectField && (
              <SoftBox sx={sectionSx}>
                <SoftTypography variant="h6" sx={{ fontSize: "1rem", mb: 0.5 }}>
                  Redirect Details
                </SoftTypography>
                <Grid container rowSpacing={0.5} columnSpacing={2} sx={{ width: "100%", ml: 0 }}>
                  <VendorFieldGrid field="complete_redirect_url" xs={12} md={6}>
                    {renderField(
                      "Complete Redirect (Status 1)",
                      <SoftInput
                        placeholder="https://quantichamps.com/survey?status=1&qsid=[identifier]"
                        value={companyForm.complete_redirect_url}
                        disabled={readOnly}
                        onChange={(event) => updateCompanyForm("complete_redirect_url", event.target.value)}
                      />
                    )}
                  </VendorFieldGrid>
                  <VendorFieldGrid field="terminate_redirect_url" xs={12} md={6}>
                    {renderField(
                      "Terminate Redirect (Status 2)",
                      <SoftInput
                        placeholder="https://quantichamps.com/survey?status=2&qsid=[identifier]"
                        value={companyForm.terminate_redirect_url}
                        disabled={readOnly}
                        onChange={(event) => updateCompanyForm("terminate_redirect_url", event.target.value)}
                      />
                    )}
                  </VendorFieldGrid>
                  <VendorFieldGrid field="over_quota_redirect_url" xs={12} md={6}>
                    {renderField(
                      "Over Quota Redirect (Status 3)",
                      <SoftInput
                        placeholder="https://quantichamps.com/survey?status=3&qsid=[identifier]"
                        value={companyForm.over_quota_redirect_url}
                        disabled={readOnly}
                        onChange={(event) => updateCompanyForm("over_quota_redirect_url", event.target.value)}
                      />
                    )}
                  </VendorFieldGrid>
                  <VendorFieldGrid field="quality_terminate_redirect_url" xs={12} md={6}>
                    {renderField(
                      "Quality Terminate Redirect (Status 4)",
                      <SoftInput
                        placeholder="https://quantichamps.com/survey?status=4&qsid=[identifier]"
                        value={companyForm.quality_terminate_redirect_url}
                        disabled={readOnly}
                        onChange={(event) => updateCompanyForm("quality_terminate_redirect_url", event.target.value)}
                      />
                    )}
                  </VendorFieldGrid>
                </Grid>
              </SoftBox>
            )}

            <SoftBox sx={sectionSx}>
              <SoftTypography variant="h6" sx={{ fontSize: "1rem", mb: 0.5 }}>
                Billing Details
              </SoftTypography>
              <Grid container rowSpacing={0.5} columnSpacing={2} sx={{ width: "100%", ml: 0 }}>
                <VendorFieldGrid field="company_name" xs={12} md={4} xl={3}>
                  {renderField(
                    "Company Name",
                    <SoftInput
                      placeholder="e.g. Acme Corporation"
                      value={companyForm.company_name}
                      disabled={readOnly}
                      onChange={(event) => updateCompanyForm("company_name", event.target.value)}
                    />
                  )}
                </VendorFieldGrid>
                <VendorFieldGrid field="billing_setting" xs={12} md={4} xl={3}>
                  {renderField(
                    "Our Country Of Invoicing",
                    renderFormSelect({
                      value: companyForm.billing_setting_id,
                      options: billingSettings.map((setting) => makeSelectOption(setting.id, setting.name)),
                      onChange: (nextValue) => updateCompanyForm("billing_setting_id", nextValue),
                    })
                  )}
                </VendorFieldGrid>
                {showVendorInvoicingCompany && (
                  <VendorFieldGrid field="invoicing_company" xs={12} md={4} xl={3}>
                    {renderField(
                      "Invoicing Company",
                      renderFormSelect({
                        value: companyForm.invoicing_company_id,
                        options: invoicingCompanies,
                        placeholder: invoicingCompaniesLoading ? "Loading companies..." : "Select Invoicing Company",
                        disabled: readOnly || invoicingCompaniesLoading,
                        onChange: (nextValue) => updateCompanyForm("invoicing_company_id", nextValue),
                      })
                    )}
                  </VendorFieldGrid>
                )}
                <VendorFieldGrid field="country" xs={12} md={4} xl={3}>
                  <SoftTypography variant="body2" sx={formLabelSx}>
                    Vendor Country
                  </SoftTypography>
                  <Stack direction="row" spacing={1} sx={fieldWithActionSx}>
                    {renderFormSelect({
                      value: companyForm.country,
                      options: vendorCountries.map((country) => makeCountryOption(country, country.name)),
                      onChange: (nextValue) => updateCompanyForm("country", nextValue),
                      formatOptionLabel: formatCountryOptionLabel,
                    })}
                  </Stack>
                </VendorFieldGrid>
                {showVendorSeZone && (
                  <VendorFieldGrid field="se_zone" xs={12} md={4} xl={3}>
                    {renderField(
                      "SE Zone",
                      renderFormSelect({
                        value: companyForm.se_zone ? "1" : "0",
                        options: [makeSelectOption("1", "Yes"), makeSelectOption("0", "No")],
                        onChange: (nextValue) => updateCompanyForm("se_zone", nextValue === "1"),
                      })
                    )}
                  </VendorFieldGrid>
                )}
                {isIndiaVendorCountry && canViewVendorField("pan_number") && (
                  <VendorFieldGrid field="pan_number" xs={12} md={4} xl={3}>
                    {renderField(
                      "PAN Number",
                      <SoftInput
                        placeholder="PAN Number"
                        value={companyForm.pan_number}
                        disabled={readOnly}
                        onChange={(event) => updateCompanyForm("pan_number", event.target.value)}
                      />
                    )}
                  </VendorFieldGrid>
                )}
                {PARTY_TAX_FIELDS_ENABLED &&
                  billingTaxes.map((tax, index) => renderBillingTaxBlock(tax, index))}
                <VendorFieldGrid field="state" xs={12} md={4} xl={3}>
                  <SoftTypography variant="body2" sx={formLabelSx}>
                    State
                  </SoftTypography>
                  <Stack direction="row" spacing={1} sx={fieldWithActionSx}>
                    {renderFormSelect({
                      value: companyForm.state,
                      options: [
                        ...(companyForm.state && !vendorStates.some((state) => state.name === companyForm.state)
                          ? [makeSelectOption(companyForm.state, companyForm.state)]
                          : []),
                        ...vendorStates.map((state) => makeSelectOption(state.name, state.name)),
                      ],
                      onChange: (nextValue) => updateCompanyForm("state", nextValue),
                      disabled: readOnly || !companyForm.country,
                    })}
                    {!readOnly && canCreateState && (
                      <Tooltip title="Add state">
                        <IconButton color="info" onClick={createVendorState} sx={fieldActionButtonSx}>
                          <AddIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Stack>
                </VendorFieldGrid>
                <VendorFieldGrid field="city" xs={12} md={4} xl={3}>
                  <SoftTypography variant="body2" sx={formLabelSx}>
                    City
                  </SoftTypography>
                  <Stack direction="row" spacing={1} sx={fieldWithActionSx}>
                    {renderFormSelect({
                      value: companyForm.city,
                      options: [
                        ...(companyForm.city && !vendorCities.some((city) => city.name === companyForm.city)
                          ? [makeSelectOption(companyForm.city, companyForm.city)]
                          : []),
                        ...vendorCities.map((city) => makeSelectOption(city.name, city.name)),
                      ],
                      onChange: (nextValue) => updateCompanyForm("city", nextValue),
                      disabled: readOnly || !companyForm.country,
                    })}
                    {!readOnly && canCreateCity && (
                      <Tooltip title="Add city">
                        <IconButton color="info" onClick={createVendorCity} sx={fieldActionButtonSx}>
                          <AddIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Stack>
                </VendorFieldGrid>
                <VendorFieldGrid field="postal_code" xs={12} md={4} xl={3}>
                  {renderField(
                    "Postal / Zip Code",
                    <SoftInput
                      placeholder="Postal / Zip Code"
                      value={companyForm.postal_code}
                      disabled={readOnly}
                      onChange={(event) => updateCompanyForm("postal_code", event.target.value)}
                    />
                  )}
                </VendorFieldGrid>
                <VendorFieldGrid field="company_address" xs={12} md={4} xl={3}>
                  {renderField(
                    "Company Address",
                    <SoftInput
                      placeholder="132, My Street, Kingston"
                      value={companyForm.company_address}
                      disabled={readOnly}
                      onChange={(event) => updateCompanyForm("company_address", event.target.value)}
                    />
                  )}
                </VendorFieldGrid>
                <VendorFieldGrid field="shipping_address" xs={12} md={4} xl={3}>
                  {renderField(
                    "Billing Address",
                    <SoftInput
                      placeholder="132, My Street, Kingston"
                      value={companyForm.shipping_address}
                      disabled={readOnly}
                      onChange={(event) => updateCompanyForm("shipping_address", event.target.value)}
                    />
                  )}
                </VendorFieldGrid>
                <VendorFieldGrid field="billing_currency" xs={12} md={4} xl={3}>
                  <SoftTypography variant="body2" sx={formLabelSx}>
                    Billing Currency
                  </SoftTypography>
                  <Stack direction="row" spacing={1} sx={fieldWithActionSx}>
                    {renderFormSelect({
                      value: companyForm.billing_currency_id,
                      options: sortedBillingCurrencies.map((currency) => makeSelectOption(currency.id, currency.code)),
                      onChange: (nextValue) => updateCompanyForm("billing_currency_id", nextValue),
                    })}
                    {!readOnly && canCreateBillingCurrency && (
                      <Tooltip title="Add billing currency">
                        <IconButton color="info" onClick={createBillingCurrency} sx={fieldActionButtonSx}>
                          <AddIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Stack>
                </VendorFieldGrid>
                <VendorFieldGrid field="billing_cycle" xs={12} md={4} xl={3}>
                  <SoftTypography variant="body2" sx={formLabelSx}>
                    Billing Cycle
                  </SoftTypography>
                  <Stack direction="row" spacing={1} sx={fieldWithActionSx}>
                    {renderFormSelect({
                      value: companyForm.billing_cycle_days,
                      options: [
                        ...(companyForm.billing_cycle_days &&
                        !billingCycles.some((cycle) => String(cycle.days) === String(companyForm.billing_cycle_days))
                          ? [makeSelectOption(companyForm.billing_cycle_days, `${companyForm.billing_cycle_days} Days`)]
                          : []),
                        ...billingCycles.map((cycle) => makeSelectOption(cycle.days, `${cycle.days} Days`)),
                      ],
                      onChange: (nextValue) => updateCompanyForm("billing_cycle_days", nextValue),
                    })}
                    {!readOnly && canCreateBillingCycle && (
                      <Tooltip title="Add billing cycle">
                        <IconButton color="info" onClick={createBillingCycle} sx={fieldActionButtonSx}>
                          <AddIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Stack>
                </VendorFieldGrid>
              </Grid>
            </SoftBox>

            {canViewAnyContactField && (
              <>
                <SoftBox mb={0.75}>
                  <SoftTypography variant="h6" sx={{ fontSize: "1rem" }}>
                    Contact Details
                  </SoftTypography>
                </SoftBox>
                {contacts.map((contact, index) => renderContactBlock(contact, index))}

                {!readOnly && canAddContact && (
                  <SoftBox display="flex" justifyContent="flex-end" mb={1}>
                    <SoftButton size="small" color="info" variant="outlined" sx={drawerActionButtonSx} startIcon={<AddIcon />} onClick={addContact}>
                      Add Contact Details
                    </SoftButton>
                  </SoftBox>
                )}
              </>
            )}
          </SoftBox>

          <Divider />
          <SoftBox display="flex" justifyContent="flex-end" gap={1} p={1.5} sx={{ backgroundColor: "#fff" }}>
            <SoftButton variant="outlined" color="dark" sx={drawerActionButtonSx} onClick={closeDrawer} disabled={submitting}>
              {readOnly ? "Close" : "Cancel"}
            </SoftButton>
            {!readOnly && ((drawerMode === "edit" && canUpdateVendor) || (drawerMode !== "edit" && canCreateVendor)) && (
              <SoftButton color="info" sx={drawerActionButtonSx} onClick={handleSubmit} disabled={submitting}>
                {submitting ? "Please wait..." : drawerMode === "edit" ? "Update Vendor" : "Save Vendor"}
              </SoftButton>
            )}
          </SoftBox>
        </SoftBox>
      </Drawer>

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Delete Vendor</DialogTitle>
        <DialogContent>
          <SoftTypography variant="body2" color="text">
            Are you sure you want to delete {selectedVendor?.name || "this vendor"}? This will soft delete the
            vendor.
          </SoftTypography>
        </DialogContent>
        <DialogActions>
          <SoftButton variant="outlined" color="dark" onClick={() => setDeleteDialogOpen(false)} disabled={submitting}>
            Cancel
          </SoftButton>
          {canDeleteVendor && (
            <SoftButton color="error" onClick={confirmDelete} disabled={submitting}>
              {submitting ? "Deleting..." : "Delete"}
            </SoftButton>
          )}
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}

export default Vendors;
